/**
 * Workspace ↔ User Group Diagnostics Page
 *
 * An educational "User Management Lab" to help developers understand:
 * - How permissions control actual access (NOT _userAttributes)
 * - Why _userAttributes is useful for UI navigation hints
 * - How one User Group can access multiple Workspaces
 * - Namespace filtering behavior with nsfilter
 * - Best practices for user management in Twinit Platform
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IafPassSvc,
  IafWorkspace,
  IafItemSvc,
  IafPermission,
  IafUserGroup
} from '@dtplatform/platform-api';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from '../../components/Header';
import './WorkspaceUserGroupDiagnosticsPage.css';

// ============================================================================
// TYPES
// ============================================================================

interface StepResult {
  stepId: string;
  stepName: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  startTime?: number;
  endTime?: number;
  elapsed?: number;
  whatThisTests: string;
  whyItMatters: string;
  whatWeExpect: string;
  apisUsed?: string[];
  permissionsApplied?: string[];
  whatActuallyHappened?: string;
  expectationDiff?: string;
  error?: string;
  data?: Record<string, unknown>;
}

interface SuiteState {
  workspace?: { _id: string; _name: string; _shortName?: string; _namespaces: string[]; _userAttributes?: Record<string, unknown> };
  workspace2?: { _id: string; _name: string; _shortName?: string; _namespaces: string[]; _userAttributes?: Record<string, unknown> };
  userGroup?: { _id: string; _name: string; _shortName?: string; _namespaces: string[]; _userAttributes?: Record<string, unknown> };
  collection?: { _id: string; _name: string; _namespaces: string[] };
  collection2?: { _id: string; _name: string; _namespaces: string[] };
  steps: StepResult[];
  isRunning: boolean;
  hasRun: boolean;
}

interface Findings {
  permissionsVsUserAttributes: string;
  multiWorkspaceAccess: string;
  namespaceFiltering: string;
  userAttributesPurpose: string;
  bestPractices: string[];
  whyCreateAccessBetweenUGs: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_SUITE_STATE: SuiteState = {
  steps: [],
  isRunning: false,
  hasRun: false
};

const STORAGE_KEYS = {
  suiteA: 'diag_suiteA_results',
  suiteB: 'diag_suiteB_results',
  suiteC: 'diag_suiteC_results',
};

// ============================================================================
// ROW-LEVEL PERMISSIONS CONSTANTS
// ============================================================================
// When a collection has this attribute set to ROW_LEVEL_PERMISSIONS_ACTIVE,
// the user group only gets CREATE permission on its items - not READ, EDIT, DELETE.
// Individual item access requires sub-resource permissions.
const ROW_LEVEL_PERMISSIONS_ATTR = '_version._userAttributes.rowLevelPermissions';
const ROW_LEVEL_PERMISSIONS_ACTIVE = 'active';

// Helper to load suite state from localStorage
const loadSuiteState = (key: string): SuiteState => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Ensure isRunning is false when loading (in case it was saved mid-run)
      return { ...parsed, isRunning: false };
    }
  } catch (e) {
    console.warn('Failed to load suite state from localStorage:', e);
  }
  return INITIAL_SUITE_STATE;
};

// Helper to save suite state to localStorage
const saveSuiteState = (key: string, state: SuiteState) => {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch (e) {
    console.warn('Failed to save suite state to localStorage:', e);
  }
};

// ============================================================================
// HELPERS
// ============================================================================

const makeCtx = (authToken: string, namespaces: string[]) => ({
  authToken,
  _namespaces: namespaces
});

const getNamespaces = (obj: { _namespaces?: string[] } | null | undefined): string[] => {
  return obj?._namespaces || [];
};

const extractFromPage = <T extends { _id: string; _name: string; _shortName?: string; _namespaces: string[]; _userAttributes?: Record<string, unknown> }>(
  result: T | { _list?: T[] } | unknown
): T => {
  if (result && typeof result === 'object' && '_list' in result && Array.isArray((result as { _list: T[] })._list)) {
    return (result as { _list: T[] })._list[0];
  }
  return result as T;
};

const formatElapsed = (ms?: number) => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

// ============================================================================
// STEP DEFINITIONS
// ============================================================================

const SUITE_A_STEPS: Omit<StepResult, 'status' | 'data'>[] = [
  {
    stepId: 'A1',
    stepName: 'Create Workspace',
    whatThisTests: 'Create a workspace with diagnostic _userType',
    whyItMatters: 'Workspaces provide namespace isolation for resources',
    whatWeExpect: 'Workspace created with server-generated namespace',
    apisUsed: ['IafPassSvc.createWorkspace()']
  },
  {
    stepId: 'A2',
    stepName: 'Create User Group',
    whatThisTests: 'Create a user group at application level',
    whyItMatters: 'User groups are namespace providers - they get their own namespace',
    whatWeExpect: 'User group created with server-generated namespace',
    apisUsed: ['IafPassSvc.createUserGroup()']
  },
  {
    stepId: 'A3',
    stepName: 'Link via _userAttributes (Both Sides)',
    whatThisTests: 'Set _userAttributes.user_group on workspace AND _userAttributes.project_workspace on group',
    whyItMatters: 'This is what ipa-core framework does - stores references in _userAttributes for UI navigation',
    whatWeExpect: 'Both references should persist. Note: This does NOT grant access - it\'s just metadata!',
    apisUsed: ['IafPassSvc.updateWorkspace()', 'IafPassSvc.updateUserGroup()', 'IafWorkspace.addUserGroups()']
  },
  {
    stepId: 'A4',
    stepName: 'Grant Permissions',
    whatThisTests: 'Grant the user group permissions to access workspace resources',
    whyItMatters: 'PERMISSIONS control access, not _userAttributes. Without this step, users cannot access resources.',
    whatWeExpect: 'Permissions created. Now users in this group can actually access the workspace.',
    apisUsed: ['IafPermission.createPassPermissions()', 'IafPermission.createItemPermissions()'],
    permissionsApplied: [
      'passportsvc:workspace:{id} - READ (read workspace)',
      'itemsvc:nameduseritem:* - READ,CREATE,EDIT,DELETE (manage collections)',
    ]
  },
  {
    stepId: 'A5',
    stepName: 'Create Test Collection',
    whatThisTests: 'Create a NamedUserCollection in the workspace namespace',
    whyItMatters: 'Proves that with permissions, we can create resources',
    whatWeExpect: 'Collection created successfully in workspace namespace',
    apisUsed: ['IafItemSvc.createNamedUserItems()']
  }
];

const SUITE_B_STEPS: Omit<StepResult, 'status' | 'data'>[] = [
  {
    stepId: 'B1',
    stepName: 'Create Workspace',
    whatThisTests: 'Create a workspace (same as Suite A)',
    whyItMatters: 'Baseline for comparison - this workspace will have NO _userAttributes linking',
    whatWeExpect: 'Workspace created with server-generated namespace',
    apisUsed: ['IafPassSvc.createWorkspace()']
  },
  {
    stepId: 'B2',
    stepName: 'Create User Group',
    whatThisTests: 'Create a user group (same as Suite A)',
    whyItMatters: 'This group will NOT have project_workspace in _userAttributes',
    whatWeExpect: 'User group created with server-generated namespace',
    apisUsed: ['IafPassSvc.createUserGroup()']
  },
  {
    stepId: 'B3',
    stepName: 'Grant Permissions (NO _userAttributes)',
    whatThisTests: 'Grant same permissions as Suite A, but WITHOUT setting _userAttributes',
    whyItMatters: 'Proves that permissions alone control access - _userAttributes is optional metadata',
    whatWeExpect: 'Permissions created. Access should work identically to Suite A.',
    apisUsed: ['IafPermission.createPassPermissions()', 'IafPermission.createItemPermissions()'],
    permissionsApplied: [
      'passportsvc:workspace:{id} - READ',
      'itemsvc:nameduseritem:* - READ,CREATE,EDIT,DELETE',
    ]
  },
  {
    stepId: 'B4',
    stepName: 'Create Test Collection',
    whatThisTests: 'Create a NamedUserCollection without _userAttributes linking',
    whyItMatters: 'Proves permissions are sufficient - _userAttributes not required for access',
    whatWeExpect: 'Collection created successfully (same as Suite A)',
    apisUsed: ['IafItemSvc.createNamedUserItems()']
  },
  {
    stepId: 'B5',
    stepName: 'Verify No _userAttributes',
    whatThisTests: 'Re-read workspace and group to confirm _userAttributes not set',
    whyItMatters: 'Confirms we intentionally skipped _userAttributes linking',
    whatWeExpect: 'workspace._userAttributes.user_group and group._userAttributes.project_workspace should be empty',
    apisUsed: ['IafPassSvc.getWorkspace()', 'IafPassSvc.getUserGroup()']
  }
];

const SUITE_C_STEPS: Omit<StepResult, 'status' | 'data'>[] = [
  {
    stepId: 'C1',
    stepName: 'Create TWO Workspaces',
    whatThisTests: 'Create WS_C1 and WS_C2 - both will be accessible by one user group',
    whyItMatters: 'Tests multi-workspace access pattern - one group accessing multiple workspaces',
    whatWeExpect: 'Two workspaces created with different namespaces',
    apisUsed: ['IafPassSvc.createWorkspace() x2']
  },
  {
    stepId: 'C2',
    stepName: 'Create ONE User Group',
    whatThisTests: 'Create a single user group that will access BOTH workspaces',
    whyItMatters: 'Proves a user group is NOT limited to one workspace - it depends on permissions',
    whatWeExpect: 'User group created',
    apisUsed: ['IafPassSvc.createUserGroup()']
  },
  {
    stepId: 'C3',
    stepName: 'Grant Permissions with Row-Level Permission Split',
    whatThisTests: 'Grant permissions for user group to access BOTH workspaces with smart item-level control',
    whyItMatters: `Creates TWO item permissions per workspace:
    1) For normal collections (rowLevelPermissions=null): Full CRUD on all items
    2) For row-level permission collections (rowLevelPermissions="active"): CREATE only - items need individual sub-resource permissions`,
    whatWeExpect: 'User group can create items anywhere, but only READ items in "active" collections if they have sub-resource permissions',
    apisUsed: ['IafPermission.createPassPermissions()', 'IafPermission.createItemPermissions()'],
    permissionsApplied: [
      'passportsvc:workspace:* → READ',
      'itemsvc:nameduseritem:* with _subresourceDesc._criteria: null → READ,CREATE,EDIT,DELETE',
      'itemsvc:nameduseritem:* with _subresourceDesc._criteria: {rowLevelPermissions: "active"} → CREATE only',
    ]
  },
  {
    stepId: 'C4',
    stepName: 'Create Collections in BOTH Workspaces',
    whatThisTests: 'Create a collection in WS_C1 and another in WS_C2',
    whyItMatters: 'Sets up data for namespace filtering tests',
    whatWeExpect: 'Two collections created, each in their respective workspace namespace',
    apisUsed: ['IafItemSvc.createNamedUserItems() x2']
  },
  {
    stepId: 'C5',
    stepName: 'Test Namespace Filtering',
    whatThisTests: 'Query with different nsfilter combinations',
    whyItMatters: 'Demonstrates how _namespaces in ctx controls what data is returned',
    whatWeExpect: 'nsfilter=[WS_C1] → only C1 data. nsfilter=[WS_C2] → only C2 data. nsfilter=[both] → all data.',
    apisUsed: ['IafItemSvc.getNamedUserItems() with different _namespaces']
  }
];

// ============================================================================
// CONCEPTS CONTENT
// ============================================================================

const CONCEPTS = {
  permissions: {
    title: '🔐 Permissions Control Access',
    content: `Permissions are what actually control whether a user can read/write/delete resources.

**Key APIs:**
- \`IafPermission.createPassPermissions()\` - Passport service resources (workspaces, user groups)
- \`IafPermission.createItemPermissions()\` - Item service resources (collections, scripts)

**Permission structure:**
\`\`\`js
{
  _actions: ['READ', 'CREATE', 'EDIT', 'DELETE'],
  _namespace: 'workspace_namespace_here',
  _resourceDesc: { _irn: 'passportsvc:workspace:*' },
  _user: { _type: 'UserGroup', _id: 'group_id' }
}
\`\`\`

Without permissions, _userAttributes linking does NOTHING for access control.`
  },
  userAttributes: {
    title: '📋 _userAttributes is UI Metadata',
    content: `_userAttributes stores references for UI navigation, NOT access control.

**Why ipa-core uses it:**
- \`workspace._userAttributes.user_group\` - List of groups "associated" with this workspace
- \`group._userAttributes.project_workspace\` - The workspace this group is "for"

**Why project_workspace is an object (not array):**
The ipa-core framework was designed with 1:1 workspace:usergroup assumption.
A better practice would be \`_userAttributes.workspaces\` as an array.

**ipa-core default _userType values:**
- Workspaces created via ipa-core use: \`_userType: "project_workspace"\`
- User groups created via ipa-core use: \`_userType: "user_group"\`
This diagnostics page uses different _userType values (diag_workspace, diag_admin_group) 
to distinguish test resources from real application data.

**Bottom line:** This is optional metadata. Permissions control actual access.`
  },
  createAccessBetweenUGs: {
    title: '🔗 Why createAccessBetweenUGs()?',
    content: `When you create multiple user groups for a project, users in one group may need to see/interact with users from other groups.

**What it does:**
For each pair of groups (A, B), it creates permissions so:
- Users in Group A can READ Group B's user group resource
- Users in Group B can READ Group A's user group resource

**Why needed:**
- User listings that show "team members"
- Permission management UIs that reference other groups
- Cross-group collaboration features

Without this, users in Group A literally cannot see that Group B exists.`
  },
  namespaceFiltering: {
    title: '🔍 Namespace Filtering (nsfilter)',
    content: `Every API call includes \`ctx._namespaces\` which filters results.

**Behavior:**
- Query with \`[namespace_A]\` → Only returns resources in namespace_A
- Query with \`[namespace_A, namespace_B]\` → Returns resources in either namespace
- Query with \`[]\` or wrong namespace → Returns nothing

**Multi-workspace access:**
A user group with permissions on MULTIPLE workspace namespaces can query across all of them by passing multiple namespaces in the ctx.

This is why portfolio views need aggregation - you must explicitly include each workspace namespace.`
  },
  rowLevelPermissions: {
    title: '🔒 Row-Level Permissions (_subresourceDesc)',
    content: `Row-level permissions control access to individual RelatedItems within a collection.

**The Split Permission Pattern:**
Create TWO item permissions for user groups:

1️⃣ **Normal collections** (\`${ROW_LEVEL_PERMISSIONS_ATTR}=null\`):
\`\`\`js
{
  _irn: 'itemsvc:nameduseritem:*',
  _subresourceDesc: { _type: 'relateditem', _criteria: null },
  _actions: ['READ', 'CREATE', 'EDIT', 'DELETE']
}
\`\`\`
→ Users get full CRUD on ALL items in these collections.

2️⃣ **Row-level permission collections** (\`${ROW_LEVEL_PERMISSIONS_ATTR}="${ROW_LEVEL_PERMISSIONS_ACTIVE}"\`):
\`\`\`js
{
  _irn: 'itemsvc:nameduseritem:*',
  _subresourceDesc: { 
    _type: 'relateditem', 
    _criteria: { "${ROW_LEVEL_PERMISSIONS_ATTR}": "${ROW_LEVEL_PERMISSIONS_ACTIVE}" }
  },
  _actions: ['CREATE']  // Only CREATE!
}
\`\`\`
→ Users can ADD items, but need individual sub-resource permissions to READ/EDIT/DELETE.

**Sub-resource permission examples:**
\`\`\`js
// By item ID
{ _criteria: { _id: "item_id_here" } }

// By users array (items with user's email in 'users' property)
{ _criteria: { users: { $in: ["user@email.com"] } } }
\`\`\`

**When to use row-level permissions:**
- Spreadsheet imports where users should only see their own rows
- Documents with per-user access control
- Any data where visibility varies by user`
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function WorkspaceUserGroupDiagnosticsPage() {
  const navigate = useNavigate();
  const { user, accessToken, application, createCtx, baseNamespaces } = useAuth();

  // State - load from localStorage if available
  const [activeTab, setActiveTab] = useState<'suiteA' | 'suiteB' | 'suiteC' | 'userTest' | 'cleanup' | 'report'>('suiteA');
  const [suiteA, setSuiteA] = useState<SuiteState>(() => loadSuiteState(STORAGE_KEYS.suiteA));
  const [suiteB, setSuiteB] = useState<SuiteState>(() => loadSuiteState(STORAGE_KEYS.suiteB));
  const [suiteC, setSuiteC] = useState<SuiteState>(() => loadSuiteState(STORAGE_KEYS.suiteC));
  const [expandedConcepts, setExpandedConcepts] = useState<Record<string, boolean>>({});
  const [findings, setFindings] = useState<Findings | null>(null);

  // Save suite states to localStorage when they change (and not running)
  useEffect(() => {
    if (!suiteA.isRunning && suiteA.hasRun) {
      saveSuiteState(STORAGE_KEYS.suiteA, suiteA);
    }
  }, [suiteA]);

  useEffect(() => {
    if (!suiteB.isRunning && suiteB.hasRun) {
      saveSuiteState(STORAGE_KEYS.suiteB, suiteB);
    }
  }, [suiteB]);

  useEffect(() => {
    if (!suiteC.isRunning && suiteC.hasRun) {
      saveSuiteState(STORAGE_KEYS.suiteC, suiteC);
    }
  }, [suiteC]);

  // User Perspective state
  const [filterDiagnosticOnly, setFilterDiagnosticOnly] = useState(true);
  const [userTestResults, setUserTestResults] = useState<{
    workspaces: Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
    userGroups: Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
    collections: Array<{ _id: string; _name: string; _namespaces: string[]; workspaceName?: string }>;
    permissions: Array<{
      _id?: string;
      _actions: string[];
      _namespace: string;
      _resourceDesc: { _irn: string };
      _user?: { _type: string; _id: string };
      source?: string; // Which namespace/resource this came from
    }>;
    userGroupMembership: Array<{ _id: string; _name: string; _namespaces: string[] }>;
    testLog: string[];
  }>({ workspaces: [], userGroups: [], collections: [], permissions: [], userGroupMembership: [], testLog: [] });
  const [isRunningUserTest, setIsRunningUserTest] = useState(false);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string; name: string; suite: 'A' | 'B' | 'C' | 'server' } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Server-fetched resources
  const [serverResources, setServerResources] = useState<{
    workspaces: Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
    userGroups: Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
  }>({ workspaces: [], userGroups: [] });
  const [isFetchingResources, setIsFetchingResources] = useState(false);

  // Related Item Permissions Lab state
  const [riLabSelectedWorkspace, setRiLabSelectedWorkspace] = useState<string>('');
  const [riLabSelectedUserGroup, setRiLabSelectedUserGroup] = useState<string>('');
  const [riLabCollection, setRiLabCollection] = useState<{
    _id: string;
    _name: string;
    _namespaces: string[];
  } | null>(null);
  const [riLabItems, setRiLabItems] = useState<Array<{
    _id: string;
    _name?: string;
    users?: string[];
    hasPermission?: boolean;
    permissionType?: 'itemId' | 'usersArray' | 'none';
    testData?: string;
    [key: string]: unknown;
  }>>([]);
  const [riLabLog, setRiLabLog] = useState<string[]>([]);
  const [riLabLoading, setRiLabLoading] = useState<string | null>(null); // Current loading action
  const [riLabShowItemsModal, setRiLabShowItemsModal] = useState(false);
  const [riLabUserGroupMembers, setRiLabUserGroupMembers] = useState<Array<{
    _id: string;
    _email?: string;
    _username?: string;
  }>>([]);
  const [riLabShareTarget, setRiLabShareTarget] = useState<Record<string, string>>({}); // itemId -> selectedUserId

  const abortRef = useRef(false);

  // ============================================================================
  // STEP RUNNER HELPER
  // ============================================================================

  const runStep = useCallback(async <T,>(
    suite: 'A' | 'B' | 'C',
    stepDef: Omit<StepResult, 'status' | 'data'>,
    fn: () => Promise<{ result: T; summary: string; expectationDiff?: string }>
  ): Promise<T | null> => {
    const setSuite = suite === 'A' ? setSuiteA : suite === 'B' ? setSuiteB : setSuiteC;

    setSuite(prev => ({
      ...prev,
      steps: prev.steps.map(s =>
        s.stepId === stepDef.stepId
          ? { ...s, status: 'running' as const, startTime: Date.now() }
          : s
      )
    }));

    try {
      const { result, summary, expectationDiff } = await fn();
      const endTime = Date.now();

      setSuite(prev => ({
        ...prev,
        steps: prev.steps.map(s =>
          s.stepId === stepDef.stepId
            ? {
                ...s,
                status: 'success' as const,
                endTime,
                elapsed: endTime - (s.startTime || endTime),
                whatActuallyHappened: summary,
                expectationDiff
              }
            : s
        )
      }));

      return result;
    } catch (error) {
      const endTime = Date.now();
      const err = error as Error;

      setSuite(prev => ({
        ...prev,
        steps: prev.steps.map(s =>
          s.stepId === stepDef.stepId
            ? {
                ...s,
                status: 'failed' as const,
                endTime,
                elapsed: endTime - (s.startTime || endTime),
                error: err.message,
                whatActuallyHappened: `FAILED: ${err.message}`
              }
            : s
        )
      }));

      return null;
    }
  }, []);

  // ============================================================================
  // SUITE A: PERMISSIONS + _userAttributes
  // ============================================================================

  const runSuiteA = useCallback(async () => {
    if (!accessToken) return;
    abortRef.current = false;

    setSuiteA({
      ...INITIAL_SUITE_STATE,
      isRunning: true,
      steps: SUITE_A_STEPS.map(s => ({ ...s, status: 'pending' as const }))
    });

    const ctxBase = makeCtx(accessToken, baseNamespaces);
    const timestamp = Date.now();

    // A1: Create Workspace
    const ws = await runStep('A', SUITE_A_STEPS[0], async () => {
      const wsResult = await IafPassSvc.createWorkspace({
        _name: `Suite A Workspace ${timestamp}`,
        _shortName: `suitea_ws_${timestamp}`.substring(0, 20),
        _namespaces: baseNamespaces,
        _userType: 'diag_workspace'
      }, ctxBase);
      const workspace = extractFromPage(wsResult);
      return {
        result: workspace,
        summary: `Created workspace: ${workspace._name}\nNamespace: ${workspace._namespaces?.[0]}`
      };
    });

    if (!ws || abortRef.current) {
      setSuiteA(prev => ({ ...prev, isRunning: false }));
      return;
    }
    setSuiteA(prev => ({ ...prev, workspace: ws }));

    // A2: Create User Group
    const ug = await runStep('A', SUITE_A_STEPS[1], async () => {
      const ugResult = await IafPassSvc.createUserGroup({
        _name: `Suite A UserGroup ${timestamp}`,
        _shortName: `suitea_ug_${timestamp}`.substring(0, 20),
        _namespaces: baseNamespaces,
        _userType: 'diag_admin_group'
      }, ctxBase);
      const userGroup = extractFromPage(ugResult);
      return {
        result: userGroup,
        summary: `Created user group: ${userGroup._name}\nNamespace: ${userGroup._namespaces?.[0]}`
      };
    });

    if (!ug || abortRef.current) {
      setSuiteA(prev => ({ ...prev, isRunning: false }));
      return;
    }
    setSuiteA(prev => ({ ...prev, userGroup: ug }));

    // A3: Link via _userAttributes
    await runStep('A', SUITE_A_STEPS[2], async () => {
      const wsNamespaces = getNamespaces(ws);
      const ctxWs = makeCtx(accessToken, wsNamespaces);

      // Update user group with project_workspace reference
      const ugUpdate = {
        ...ug,
        _userAttributes: {
          ...(ug._userAttributes || {}),
          project_workspace: { _id: ws._id, _namespaces: wsNamespaces }
        }
      };
      await IafPassSvc.updateUserGroup(ug._id, ugUpdate, ctxBase);

      // Update workspace with user_group reference via IafWorkspace.addUserGroups
      // This is what ipa-core does internally
      await IafWorkspace.addUserGroups(ws, [ug], ctxWs);

      // Re-read to verify
      const wsRefreshed = await IafPassSvc.getWorkspace(ws._id, ctxWs);
      const ugRefreshed = await IafPassSvc.getUserGroup(ug._id, ctxBase);

      const wsHasUG = Array.isArray(wsRefreshed?._userAttributes?.user_group);
      const ugHasPW = !!ugRefreshed?._userAttributes?.project_workspace;

      return {
        result: { wsRefreshed, ugRefreshed },
        summary: `_userAttributes linking complete:\n` +
          `• workspace._userAttributes.user_group: ${wsHasUG ? '✅ SET' : '❌ NOT SET'}\n` +
          `• group._userAttributes.project_workspace: ${ugHasPW ? '✅ SET' : '❌ NOT SET'}\n\n` +
          `⚠️ Note: This is just METADATA - it does NOT grant access!`,
        expectationDiff: wsHasUG && ugHasPW ? 'Both sides linked as expected' : 'Some links may not have persisted'
      };
    });

    if (abortRef.current) {
      setSuiteA(prev => ({ ...prev, isRunning: false }));
      return;
    }

    // A4: Grant Permissions
    await runStep('A', SUITE_A_STEPS[3], async () => {
      const wsNamespaces = getNamespaces(ws);

      // Create permission for user group to read workspace
      const wsPermission = {
        _actions: ['READ'],
        _namespace: wsNamespaces[0],
        _resourceDesc: { _irn: `passportsvc:workspace:${ws._id}` },
        _user: { _type: 'usergroup', _id: ug._id }
      };

      // Create permission for user group to manage items in workspace
      const itemPermission = {
        _actions: ['READ', 'CREATE', 'EDIT', 'DELETE'],
        _namespace: wsNamespaces[0],
        _resourceDesc: { _irn: 'itemsvc:nameduseritem:*' },
        _user: { _type: 'usergroup', _id: ug._id }
      };

      await IafPermission.createPassPermissions([wsPermission], ctxBase);
      await IafPermission.createItemPermissions([itemPermission], ctxBase);

      return {
        result: { wsPermission, itemPermission },
        summary: `Permissions granted:\n` +
          `• READ on passportsvc:workspace:${ws._id}\n` +
          `• READ,CREATE,EDIT,DELETE on itemsvc:nameduseritem:*\n\n` +
          `✅ NOW users in this group can actually access the workspace!`,
        expectationDiff: 'Permissions should enable resource access'
      };
    });

    if (abortRef.current) {
      setSuiteA(prev => ({ ...prev, isRunning: false }));
      return;
    }

    // A5: Create Test Collection
    const coll = await runStep('A', SUITE_A_STEPS[4], async () => {
      const wsNamespaces = getNamespaces(ws);
      const ctxWs = makeCtx(accessToken, wsNamespaces);

      const collResult = await IafItemSvc.createNamedUserItems([{
        _name: `Suite A Collection ${timestamp}`,
        _shortName: `suitea_coll_${timestamp}`.substring(0, 20),
        _namespaces: wsNamespaces,
        _userType: 'diag_test_collection'
      }], 'NamedUserCollection', ctxWs);

      const collection = collResult?._list?.[0];

      return {
        result: collection,
        summary: `Created collection: ${collection?._name}\nIn namespace: ${wsNamespaces[0]}\n\n` +
          `✅ Suite A complete: Workspace + UserGroup + _userAttributes + Permissions + Collection`
      };
    });

    if (coll) {
      setSuiteA(prev => ({ ...prev, collection: coll }));
    }

    setSuiteA(prev => ({ ...prev, isRunning: false, hasRun: true }));
  }, [accessToken, baseNamespaces, runStep]);

  // Retry function for Suite A Step 4 (Grant Permissions)
  const retrySuiteAStep4 = useCallback(async () => {
    if (!accessToken || !suiteA.workspace || !suiteA.userGroup) return;

    const ws = suiteA.workspace;
    const ug = suiteA.userGroup;
    const ctxBase = makeCtx(accessToken, baseNamespaces);

    await runStep('A', SUITE_A_STEPS[3], async () => {
      const wsNamespaces = getNamespaces(ws);

      const wsPermission = {
        _actions: ['READ'],
        _namespace: wsNamespaces[0],
        _resourceDesc: { _irn: `passportsvc:workspace:${ws._id}` },
        _user: { _type: 'usergroup', _id: ug._id }
      };

      const itemPermission = {
        _actions: ['READ', 'CREATE', 'EDIT', 'DELETE'],
        _namespace: wsNamespaces[0],
        _resourceDesc: { _irn: 'itemsvc:nameduseritem:*' },
        _user: { _type: 'usergroup', _id: ug._id }
      };

      await IafPermission.createPassPermissions([wsPermission], ctxBase);
      await IafPermission.createItemPermissions([itemPermission], ctxBase);

      return {
        result: { wsPermission, itemPermission },
        summary: `Permissions granted:\n` +
          `• READ on passportsvc:workspace:${ws._id}\n` +
          `• READ,CREATE,EDIT,DELETE on itemsvc:nameduseritem:*\n\n` +
          `✅ NOW users in this group can actually access the workspace!`,
        expectationDiff: 'Permissions should enable resource access'
      };
    });
  }, [accessToken, baseNamespaces, suiteA.workspace, suiteA.userGroup, runStep]);

  // Clear suite results and localStorage
  const clearSuiteResults = useCallback((suite: 'A' | 'B' | 'C' | 'all') => {
    if (suite === 'A' || suite === 'all') {
      setSuiteA(INITIAL_SUITE_STATE);
      localStorage.removeItem(STORAGE_KEYS.suiteA);
    }
    if (suite === 'B' || suite === 'all') {
      setSuiteB(INITIAL_SUITE_STATE);
      localStorage.removeItem(STORAGE_KEYS.suiteB);
    }
    if (suite === 'C' || suite === 'all') {
      setSuiteC(INITIAL_SUITE_STATE);
      localStorage.removeItem(STORAGE_KEYS.suiteC);
    }
  }, []);

  // ============================================================================
  // SUITE B: PERMISSIONS ONLY (NO _userAttributes)
  // ============================================================================

  const runSuiteB = useCallback(async () => {
    if (!accessToken) return;
    abortRef.current = false;

    setSuiteB({
      ...INITIAL_SUITE_STATE,
      isRunning: true,
      steps: SUITE_B_STEPS.map(s => ({ ...s, status: 'pending' as const }))
    });

    const ctxBase = makeCtx(accessToken, baseNamespaces);
    const timestamp = Date.now();

    // B1: Create Workspace
    const ws = await runStep('B', SUITE_B_STEPS[0], async () => {
      const wsResult = await IafPassSvc.createWorkspace({
        _name: `Suite B Workspace (No UA) ${timestamp}`,
        _shortName: `suiteb_ws_${timestamp}`.substring(0, 20),
        _namespaces: baseNamespaces,
        _userType: 'diag_workspace'
      }, ctxBase);
      const workspace = extractFromPage(wsResult);
      return {
        result: workspace,
        summary: `Created workspace: ${workspace._name}\nNamespace: ${workspace._namespaces?.[0]}`
      };
    });

    if (!ws || abortRef.current) {
      setSuiteB(prev => ({ ...prev, isRunning: false }));
      return;
    }
    setSuiteB(prev => ({ ...prev, workspace: ws }));

    // B2: Create User Group
    const ug = await runStep('B', SUITE_B_STEPS[1], async () => {
      const ugResult = await IafPassSvc.createUserGroup({
        _name: `Suite B UserGroup (No UA) ${timestamp}`,
        _shortName: `suiteb_ug_${timestamp}`.substring(0, 20),
        _namespaces: baseNamespaces,
        _userType: 'diag_admin_group'
      }, ctxBase);
      const userGroup = extractFromPage(ugResult);
      return {
        result: userGroup,
        summary: `Created user group: ${userGroup._name}\n⚠️ Will NOT set _userAttributes linking`
      };
    });

    if (!ug || abortRef.current) {
      setSuiteB(prev => ({ ...prev, isRunning: false }));
      return;
    }
    setSuiteB(prev => ({ ...prev, userGroup: ug }));

    // B3: Grant Permissions (NO _userAttributes)
    await runStep('B', SUITE_B_STEPS[2], async () => {
      const wsNamespaces = getNamespaces(ws);

      const wsPermission = {
        _actions: ['READ'],
        _namespace: wsNamespaces[0],
        _resourceDesc: { _irn: `passportsvc:workspace:${ws._id}` },
        _user: { _type: 'usergroup', _id: ug._id }
      };

      const itemPermission = {
        _actions: ['READ', 'CREATE', 'EDIT', 'DELETE'],
        _namespace: wsNamespaces[0],
        _resourceDesc: { _irn: 'itemsvc:nameduseritem:*' },
        _user: { _type: 'usergroup', _id: ug._id }
      };

      await IafPermission.createPassPermissions([wsPermission], ctxBase);
      await IafPermission.createItemPermissions([itemPermission], ctxBase);

      return {
        result: { wsPermission, itemPermission },
        summary: `Permissions granted (SAME as Suite A):\n` +
          `• READ on passportsvc:workspace:${ws._id}\n` +
          `• READ,CREATE,EDIT,DELETE on itemsvc:nameduseritem:*\n\n` +
          `⚠️ NO _userAttributes linking done!\n` +
          `If this works, it proves permissions alone control access.`
      };
    });

    if (abortRef.current) {
      setSuiteB(prev => ({ ...prev, isRunning: false }));
      return;
    }

    // B4: Create Test Collection
    const coll = await runStep('B', SUITE_B_STEPS[3], async () => {
      const wsNamespaces = getNamespaces(ws);
      const ctxWs = makeCtx(accessToken, wsNamespaces);

      const collResult = await IafItemSvc.createNamedUserItems([{
        _name: `Suite B Collection (No UA) ${timestamp}`,
        _shortName: `suiteb_coll_${timestamp}`.substring(0, 20),
        _namespaces: wsNamespaces,
        _userType: 'diag_test_collection'
      }], 'NamedUserCollection', ctxWs);

      const collection = collResult?._list?.[0];

      return {
        result: collection,
        summary: collection
          ? `✅ Created collection WITHOUT _userAttributes linking!\n\nThis proves: PERMISSIONS control access, not _userAttributes.`
          : `❌ Failed to create collection`
      };
    });

    if (coll) {
      setSuiteB(prev => ({ ...prev, collection: coll }));
    }

    if (abortRef.current) {
      setSuiteB(prev => ({ ...prev, isRunning: false }));
      return;
    }

    // B5: Verify No _userAttributes
    await runStep('B', SUITE_B_STEPS[4], async () => {
      const wsNamespaces = getNamespaces(ws);
      const ctxWs = makeCtx(accessToken, wsNamespaces);

      const wsRefreshed = await IafPassSvc.getWorkspace(ws._id, ctxWs);
      const ugRefreshed = await IafPassSvc.getUserGroup(ug._id, ctxBase);

      const wsHasUG = Array.isArray(wsRefreshed?._userAttributes?.user_group) && 
                      (wsRefreshed._userAttributes.user_group as unknown[]).length > 0;
      const ugHasPW = !!ugRefreshed?._userAttributes?.project_workspace;

      return {
        result: { wsRefreshed, ugRefreshed },
        summary: `_userAttributes verification:\n` +
          `• workspace._userAttributes.user_group: ${wsHasUG ? '⚠️ SET (unexpected!)' : '✅ EMPTY (as expected)'}\n` +
          `• group._userAttributes.project_workspace: ${ugHasPW ? '⚠️ SET (unexpected!)' : '✅ EMPTY (as expected)'}\n\n` +
          `Suite B complete: Workspace + UserGroup + Permissions + Collection (NO _userAttributes)`,
        expectationDiff: !wsHasUG && !ugHasPW 
          ? 'Confirmed: _userAttributes not required for access'
          : 'Unexpected: _userAttributes was set somewhere'
      };
    });

    setSuiteB(prev => ({ ...prev, isRunning: false, hasRun: true }));
  }, [accessToken, baseNamespaces, runStep]);

  // ============================================================================
  // SUITE C: ONE USER GROUP → TWO WORKSPACES
  // ============================================================================

  const runSuiteC = useCallback(async () => {
    if (!accessToken) return;
    abortRef.current = false;

    setSuiteC({
      ...INITIAL_SUITE_STATE,
      isRunning: true,
      steps: SUITE_C_STEPS.map(s => ({ ...s, status: 'pending' as const }))
    });

    const ctxBase = makeCtx(accessToken, baseNamespaces);
    const timestamp = Date.now();

    // C1: Create TWO Workspaces
    const wsResult = await runStep('C', SUITE_C_STEPS[0], async () => {
      const ws1Result = await IafPassSvc.createWorkspace({
        _name: `Suite C Workspace 1 ${timestamp}`,
        _shortName: `suitec_ws1_${timestamp}`.substring(0, 20),
        _namespaces: baseNamespaces,
        _userType: 'diag_workspace'
      }, ctxBase);
      const ws1 = extractFromPage(ws1Result);

      const ws2Result = await IafPassSvc.createWorkspace({
        _name: `Suite C Workspace 2 ${timestamp}`,
        _shortName: `suitec_ws2_${timestamp}`.substring(0, 20),
        _namespaces: baseNamespaces,
        _userType: 'diag_workspace'
      }, ctxBase);
      const ws2 = extractFromPage(ws2Result);

      return {
        result: { ws1, ws2 },
        summary: `Created TWO workspaces:\n` +
          `• WS_C1: ${ws1._name} [${ws1._namespaces?.[0]}]\n` +
          `• WS_C2: ${ws2._name} [${ws2._namespaces?.[0]}]`
      };
    });

    if (!wsResult || abortRef.current) {
      setSuiteC(prev => ({ ...prev, isRunning: false }));
      return;
    }
    setSuiteC(prev => ({ ...prev, workspace: wsResult.ws1, workspace2: wsResult.ws2 }));

    // C2: Create ONE User Group
    const ug = await runStep('C', SUITE_C_STEPS[1], async () => {
      const ugResult = await IafPassSvc.createUserGroup({
        _name: `Suite C Multi-WS Group ${timestamp}`,
        _shortName: `suitec_ug_${timestamp}`.substring(0, 20),
        _namespaces: baseNamespaces,
        _userType: 'diag_admin_group'
      }, ctxBase);
      const userGroup = extractFromPage(ugResult);
      return {
        result: userGroup,
        summary: `Created ONE user group: ${userGroup._name}\n\n` +
          `This group will access BOTH workspaces via permissions.`
      };
    });

    if (!ug || abortRef.current) {
      setSuiteC(prev => ({ ...prev, isRunning: false }));
      return;
    }
    setSuiteC(prev => ({ ...prev, userGroup: ug }));

    // C3: Grant Permissions with Row-Level Permission Split for BOTH Workspaces
    await runStep('C', SUITE_C_STEPS[2], async () => {
      const ws1Ns = getNamespaces(wsResult.ws1);
      const ws2Ns = getNamespaces(wsResult.ws2);

      // Helper to create item permissions with row-level split
      const createItemPermissionsWithRowLevelSplit = async (wsNs: string) => {
        // Permission 1: For collections WITHOUT rowLevelPermissions (null criteria)
        // Users get full CRUD on all items in "normal" collections
        await IafPermission.createItemPermissions([{
          _actions: ['READ', 'CREATE', 'EDIT', 'DELETE'],
          _namespace: wsNs,
          _resourceDesc: { 
            _irn: 'itemsvc:nameduseritem:*',
            _criteria: { [ROW_LEVEL_PERMISSIONS_ATTR]: null }  // Matches items in collections where rowLevelPermissions is not set            
          },
          _user: { _type: 'usergroup', _id: ug._id }
        }], ctxBase);

        //Permission profile used by orchestrator/workflow to create the collection
        const permissionProfile = {
          _actions: ['READ', 'CREATE', 'EDIT', 'DELETE', 'SHARE'],
          _namespace: wsNs,
          _resourceDesc: {
            _irn: 'itemsvc:nameduseritem:*',
            _criteria: { [ROW_LEVEL_PERMISSIONS_ATTR]: ROW_LEVEL_PERMISSIONS_ACTIVE }
          },
          _user: { _type: 'permissionprofile', _id: user?._id }
        };
        const permissionProfileResult = await IafPermission.createItemPermissions([permissionProfile], ctxBase, user);
        const permissionProfileId = permissionProfileResult?._list?.[0]?._id;
        if (!permissionProfileId) {
          throw new Error('Failed to create permission profile');
        }

        // Permission 2: For collections WITH rowLevelPermissions="active"
        // Users only can CREATE via permission profile + orchestrators
        await IafPermission.createItemPermissions([{
          _actions: ['READ', 'CREATE', 'EDIT', 'DELETE', 'SHARE'],  // Only CREATE - not READ, EDIT, DELETE
          _namespace: wsNs,
          _resourceDesc: {
            _irn: 'itemsvc:nameduseritem:*',
            _criteria: { [ROW_LEVEL_PERMISSIONS_ATTR]: ROW_LEVEL_PERMISSIONS_ACTIVE },            
          },
          //onlu user who created the user group can create row level permitted collection
          _user: { 
            _type: 'permissionprofile', 
            _id: permissionProfileId
          }
        }], ctxBase, user);


        // Permission 2: For collections WITH rowLevelPermissions="active"
        // Users only get CREATE, and only sub resource
        // Permission profile used by orchestrator/workflow to 
        // permission profile + orchestrator + custom script = will create the collection
        // permission profile + orchestrator + custom script = will edit, delete the collection
        // permission profile + orchestrator + custom script = will share the relateditem
        // not needed in this example: permission profile + orchestrator + custom script = will change permissions on sub resource
        // permission profile based on user id as side effect of CREATING relateditem will add READ, EDIT, DELETE, SHARE permissions on sub resource
        await IafPermission.createItemPermissions([{
          _actions: ['CREATE'],  // Only SHARE on sub resource - allowed to update permissions
          _namespace: wsNs,
          _resourceDesc: {
            _irn: 'itemsvc:nameduseritem:*',
            _criteria: { [ROW_LEVEL_PERMISSIONS_ATTR]: ROW_LEVEL_PERMISSIONS_ACTIVE },            
            _subresourceDesc: {
              _type: 'relateditem',
            },
          },
          _user: { _type: 'usergroup', _id: ug._id }
        }], ctxBase);


        
      };

      // Permissions for WS1
      await IafPermission.createPassPermissions([{
        _actions: ['READ'],
        _namespace: ws1Ns[0],
        _resourceDesc: { _irn: `passportsvc:workspace:${wsResult.ws1._id}` },
        _user: { _type: 'usergroup', _id: ug._id }
      }], ctxBase);

      await createItemPermissionsWithRowLevelSplit(ws1Ns[0]);

      // Permissions for WS2
      await IafPermission.createPassPermissions([{
        _actions: ['READ'],
        _namespace: ws2Ns[0],
        _resourceDesc: { _irn: `passportsvc:workspace:${wsResult.ws2._id}` },
        _user: { _type: 'usergroup', _id: ug._id }
      }], ctxBase);

      await createItemPermissionsWithRowLevelSplit(ws2Ns[0]);

      return {
        result: { ws1Ns, ws2Ns },
        summary: `Granted permissions with ROW-LEVEL PERMISSION SPLIT on BOTH workspace namespaces:\n\n` +
          `For EACH workspace (${ws1Ns[0]} and ${ws2Ns[0]}):\n` +
          `  • READ passportsvc:workspace:*\n\n` +
          `  📁 Normal Collections (${ROW_LEVEL_PERMISSIONS_ATTR}=null):\n` +
          `     → READ,CREATE,EDIT,DELETE on all items\n\n` +
          `  🔒 Row-Level Permission Collections (${ROW_LEVEL_PERMISSIONS_ATTR}="${ROW_LEVEL_PERMISSIONS_ACTIVE}"):\n` +
          `     → CREATE only - individual items require sub-resource permissions\n\n` +
          `✅ User group now has smart row-level permission control!`
      };
    });

    if (abortRef.current) {
      setSuiteC(prev => ({ ...prev, isRunning: false }));
      return;
    }

    // C4: Create Collections in BOTH Workspaces
    const collsResult = await runStep('C', SUITE_C_STEPS[3], async () => {
      const ws1Ns = getNamespaces(wsResult.ws1);
      const ws2Ns = getNamespaces(wsResult.ws2);
      const ctxWs1 = makeCtx(accessToken, ws1Ns);
      const ctxWs2 = makeCtx(accessToken, ws2Ns);

      const coll1Result = await IafItemSvc.createNamedUserItems([{
        _name: `Suite C Collection in WS1 ${timestamp}`,
        _shortName: `suitec_c1_${timestamp}`.substring(0, 20),
        _namespaces: ws1Ns,
        _userType: 'diag_test_collection'
      }], 'NamedUserCollection', ctxWs1);
      const coll1 = coll1Result?._list?.[0];

      const coll2Result = await IafItemSvc.createNamedUserItems([{
        _name: `Suite C Collection in WS2 ${timestamp}`,
        _shortName: `suitec_c2_${timestamp}`.substring(0, 20),
        _namespaces: ws2Ns,
        _userType: 'diag_test_collection'
      }], 'NamedUserCollection', ctxWs2);
      const coll2 = coll2Result?._list?.[0];

      return {
        result: { coll1, coll2 },
        summary: `Created collections in BOTH workspaces:\n` +
          `• Collection 1 in WS_C1: ${coll1?._name}\n` +
          `• Collection 2 in WS_C2: ${coll2?._name}`
      };
    });

    if (collsResult) {
      setSuiteC(prev => ({ ...prev, collection: collsResult.coll1, collection2: collsResult.coll2 }));
    }

    if (abortRef.current) {
      setSuiteC(prev => ({ ...prev, isRunning: false }));
      return;
    }

    // C5: Test Namespace Filtering
    await runStep('C', SUITE_C_STEPS[4], async () => {
      const ws1Ns = getNamespaces(wsResult.ws1);
      const ws2Ns = getNamespaces(wsResult.ws2);

      // Query with WS1 namespace only
      const ctxWs1 = makeCtx(accessToken, ws1Ns);
      const result1 = await IafItemSvc.getNamedUserItems(
        { _userType: 'diag_test_collection' },
        ctxWs1,
        { _pageSize: 100 }
      );
      const count1 = result1?._list?.length || 0;

      // Query with WS2 namespace only
      const ctxWs2 = makeCtx(accessToken, ws2Ns);
      const result2 = await IafItemSvc.getNamedUserItems(
        { _userType: 'diag_test_collection' },
        ctxWs2,
        { _pageSize: 100 }
      );
      const count2 = result2?._list?.length || 0;

      // Query with BOTH namespaces
      const ctxBoth = makeCtx(accessToken, [...ws1Ns, ...ws2Ns]);
      const resultBoth = await IafItemSvc.getNamedUserItems(
        { _userType: 'diag_test_collection' },
        ctxBoth,
        { _pageSize: 100 }
      );
      const countBoth = resultBoth?._list?.length || 0;

      return {
        result: { count1, count2, countBoth },
        summary: `🔍 Namespace Filtering Results:\n\n` +
          `Query with nsfilter=[${ws1Ns[0]}]:\n` +
          `  → Found ${count1} collection(s)\n\n` +
          `Query with nsfilter=[${ws2Ns[0]}]:\n` +
          `  → Found ${count2} collection(s)\n\n` +
          `Query with nsfilter=[${ws1Ns[0]}, ${ws2Ns[0]}]:\n` +
          `  → Found ${countBoth} collection(s)\n\n` +
          `✅ Suite C complete: One user group can access multiple workspaces!\n` +
          `The nsfilter in ctx controls which workspace data is returned.`,
        expectationDiff: count1 >= 1 && count2 >= 1 && countBoth >= 2
          ? 'Multi-workspace access confirmed!'
          : 'Check results - expected to find collections in both workspaces'
      };
    });

    setSuiteC(prev => ({ ...prev, isRunning: false, hasRun: true }));
  }, [accessToken, baseNamespaces, runStep]);

  // ============================================================================
  // USER PERSPECTIVE: See what a user can access
  // ============================================================================

  const addUserTestLog = useCallback((msg: string) => {
    setUserTestResults(prev => ({
      ...prev,
      testLog: [...prev.testLog, `[${new Date().toISOString().substring(11, 19)}] ${msg}`]
    }));
  }, []);

  const runUserPerspectiveTests = useCallback(async () => {
    if (!accessToken) return;

    setIsRunningUserTest(true);
    setUserTestResults({ workspaces: [], userGroups: [], collections: [], permissions: [], userGroupMembership: [], testLog: [] });

    const ctx = createCtx(baseNamespaces);

    addUserTestLog('🔍 Fetching visible workspaces...');
    try {
      // Fetch all and filter client-side (API query may not work as expected)
      const wsResult = await IafPassSvc.getWorkspaces({}, ctx, { _pageSize: 500 });
      const allWorkspaces = (wsResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
      const workspaces = filterDiagnosticOnly 
        ? allWorkspaces.filter(ws => ws._userType === 'diag_workspace')
        : allWorkspaces;
      addUserTestLog(`  Found ${workspaces.length} workspace(s)${filterDiagnosticOnly ? ' (filtered to diag_workspace)' : ''}`);
      setUserTestResults(prev => ({ ...prev, workspaces }));
    } catch (err) {
      addUserTestLog(`  ❌ Error: ${(err as Error).message}`);
    }

    addUserTestLog('👥 Fetching visible user groups...');
    try {
      // Fetch all and filter client-side
      const ugResult = await IafPassSvc.getUserGroups({}, ctx, { _pageSize: 500 });
      const allUserGroups = (ugResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
      const userGroups = filterDiagnosticOnly 
        ? allUserGroups.filter(ug => ug._userType === 'diag_admin_group' || ug._userType === 'diag_limited_group')
        : allUserGroups;
      addUserTestLog(`  Found ${userGroups.length} user group(s)${filterDiagnosticOnly ? ' (filtered to diag_admin_group/diag_limited_group)' : ''}`);
      setUserTestResults(prev => ({ ...prev, userGroups }));
    } catch (err) {
      addUserTestLog(`  ❌ Error: ${(err as Error).message}`);
    }

    addUserTestLog('📦 Fetching visible collections...');
    try {
      // Get collections from all visible workspaces
      const allCollections: Array<{ _id: string; _name: string; _namespaces: string[]; workspaceName?: string }> = [];
      
      // Get diagnostic workspaces only for collections
      const wsResult = await IafPassSvc.getWorkspaces({}, ctx, { _pageSize: 500 });
      const allWorkspaces = (wsResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
      const workspaces = filterDiagnosticOnly 
        ? allWorkspaces.filter(ws => ws._userType === 'diag_workspace')
        : allWorkspaces;

      for (const ws of workspaces) {
        if (!ws._namespaces?.[0]) continue;
        const wsCtx = createCtx([ws._namespaces[0]]);
        try {
          const collResult = await IafItemSvc.getNamedUserItems(
            { _userType: 'diag_test_collection' },
            wsCtx,
            { _pageSize: 100 }
          );
          const collections = (collResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[] }>;
          collections.forEach(c => allCollections.push({ ...c, workspaceName: ws._name }));
        } catch {
          // May not have access to this workspace
        }
      }
      
      addUserTestLog(`  Found ${allCollections.length} collection(s) across ${workspaces.length} workspace(s)`);
      setUserTestResults(prev => ({ ...prev, collections: allCollections }));
    } catch (err) {
      addUserTestLog(`  ❌ Error: ${(err as Error).message}`);
    }

    // Fetch permissions from all visible namespaces (workspaces + user groups)
    addUserTestLog('🔐 Fetching permissions...');
    try {
      const allPermissions: Array<{
        _id?: string;
        _actions: string[];
        _namespace: string;
        _resourceDesc: { _irn: string };
        _user?: { _type: string; _id: string };
        source?: string;
      }> = [];

      // Get the current workspaces and user groups for namespace context
      const wsResult = await IafPassSvc.getWorkspaces({}, ctx, { _pageSize: 500 });
      const allWorkspaces = (wsResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
      const workspaces = filterDiagnosticOnly 
        ? allWorkspaces.filter(ws => ws._userType === 'diag_workspace')
        : allWorkspaces;

      const ugResult = await IafPassSvc.getUserGroups({}, ctx, { _pageSize: 500 });
      const allUserGroups = (ugResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
      const userGroups = filterDiagnosticOnly 
        ? allUserGroups.filter(ug => ug._userType === 'diag_admin_group' || ug._userType === 'diag_limited_group')
        : allUserGroups;

      // Fetch permissions from each workspace namespace
      for (const ws of workspaces) {
        if (!ws._namespaces?.[0]) continue;
        const wsNs = ws._namespaces[0];
        const wsCtx = makeCtx(accessToken, [wsNs]);
        
        try {
          // Get passport service permissions - use wildcard IRN to get all
          const passPerms = await IafPermission.getPassPermissions(
            { _namespace: wsNs, '_resourceDesc._irn': 'passportsvc:*:*' },
            wsCtx,
            { _pageSize: 100 }
          );
          const passList = (passPerms?._list || passPerms || []) as Array<{
            _id?: string;
            _actions: string[];
            _namespace: string;
            _resourceDesc: { _irn: string };
            _user?: { _type: string; _id: string };
          }>;
          passList.forEach(p => allPermissions.push({ ...p, source: `WS: ${ws._name}` }));
        } catch (err) {
          addUserTestLog(`  ⚠️ Could not fetch pass permissions from workspace ${ws._name}: ${(err as Error).message}`);
        }

        try {
          // Get item service permissions - use wildcard IRN to get all
          const itemPerms = await IafPermission.getItemPermissions(
            { _namespace: wsNs, '_resourceDesc._irn': 'itemsvc:*:*' },
            wsCtx,
            { _pageSize: 100 }
          );
          const itemList = (itemPerms?._list || itemPerms || []) as Array<{
            _id?: string;
            _actions: string[];
            _namespace: string;
            _resourceDesc: { _irn: string };
            _user?: { _type: string; _id: string };
          }>;
          itemList.forEach(p => allPermissions.push({ ...p, source: `WS: ${ws._name}` }));
        } catch (err) {
          addUserTestLog(`  ⚠️ Could not fetch item permissions from workspace ${ws._name}: ${(err as Error).message}`);
        }
      }

      // Also fetch permissions from user group namespaces
      for (const ug of userGroups) {
        if (!ug._namespaces?.[0]) continue;
        const ugNs = ug._namespaces[0];
        const ugCtx = makeCtx(accessToken, [ugNs]);
        
        try {
          // Get passport service permissions from user group namespace
          const passPerms = await IafPermission.getPassPermissions(
            { _namespace: ugNs, '_resourceDesc._irn': 'passportsvc:*:*' },
            ugCtx,
            { _pageSize: 100 }
          );
          const passList = (passPerms?._list || passPerms || []) as Array<{
            _id?: string;
            _actions: string[];
            _namespace: string;
            _resourceDesc: { _irn: string };
            _user?: { _type: string; _id: string };
          }>;
          passList.forEach(p => allPermissions.push({ ...p, source: `UG: ${ug._name}` }));
        } catch (err) {
          addUserTestLog(`  ⚠️ Could not fetch pass permissions from user group ${ug._name}: ${(err as Error).message}`);
        }

        try {
          // Get item service permissions from user group namespace
          const itemPerms = await IafPermission.getItemPermissions(
            { _namespace: ugNs, '_resourceDesc._irn': 'itemsvc:*:*' },
            ugCtx,
            { _pageSize: 100 }
          );
          const itemList = (itemPerms?._list || itemPerms || []) as Array<{
            _id?: string;
            _actions: string[];
            _namespace: string;
            _resourceDesc: { _irn: string };
            _user?: { _type: string; _id: string };
          }>;
          itemList.forEach(p => allPermissions.push({ ...p, source: `UG: ${ug._name}` }));
        } catch (err) {
          addUserTestLog(`  ⚠️ Could not fetch item permissions from user group ${ug._name}: ${(err as Error).message}`);
        }
      }

      addUserTestLog(`  Found ${allPermissions.length} permission(s)`);
      setUserTestResults(prev => ({ ...prev, permissions: allPermissions }));
    } catch (err) {
      addUserTestLog(`  ❌ Error fetching permissions: ${(err as Error).message}`);
    }

    // Fetch user group membership (which user groups does the current user belong to)
    addUserTestLog('👤 Checking user group membership...');
    try {
      // User groups user is member of will be accessible
      const ugResult = await IafPassSvc.getUserGroups({}, ctx, { _pageSize: 500 });
      const membership = (ugResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
      // For now, list all visible user groups as potential memberships
      addUserTestLog(`  User can access ${membership.length} user group(s)`);
      setUserTestResults(prev => ({ ...prev, userGroupMembership: membership }));
    } catch (err) {
      addUserTestLog(`  ❌ Error: ${(err as Error).message}`);
    }

    addUserTestLog('✅ User perspective test complete');
    setIsRunningUserTest(false);
  }, [accessToken, baseNamespaces, createCtx, filterDiagnosticOnly, addUserTestLog]);

  // ============================================================================
  // RELATED ITEM PERMISSIONS LAB
  // ============================================================================

  const addRiLabLog = useCallback((msg: string) => {
    setRiLabLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  }, []);

  // Get selected workspace and user group objects
  const selectedWsObj = useMemo(() => 
    userTestResults.workspaces.find(ws => ws._id === riLabSelectedWorkspace),
    [userTestResults.workspaces, riLabSelectedWorkspace]
  );
  
  const selectedUgObj = useMemo(() => 
    userTestResults.userGroups.find(ug => ug._id === riLabSelectedUserGroup),
    [userTestResults.userGroups, riLabSelectedUserGroup]
  );

  // Auto-detect existing collection when workspace is selected
  useEffect(() => {
    const detectExistingCollection = async () => {
      if (!accessToken || !selectedWsObj) {
        setRiLabCollection(null);
        return;
      }
      
      const wsNs = selectedWsObj._namespaces[0];
      if (!wsNs) return;
      
      const ctx = makeCtx(accessToken, [wsNs]);
      
      try {
        const existingResult = await IafItemSvc.getNamedUserItems(
          { query: {_userType: 'ri_permission_test_collection' }} ,
          ctx,
          { _pageSize: 10 }
        );
        console.log('detect Existing ri_permission_test_collection Collection', existingResult);
        const existingList = (existingResult?._list || existingResult || []) as Array<{ _id: string; _name: string; _namespaces: string[] }>;
        
        if (existingList.length > 0) {
          const existing = existingList[0];
          setRiLabCollection(existing);
          addRiLabLog(`🔍 Found existing collection: ${existing._name}`);
        } else {
          setRiLabCollection(null);
        }
      } catch (err) {
        console.log('Could not check for existing collection:', err);
        setRiLabCollection(null);
      }
    };
    
    detectExistingCollection();
  }, [accessToken, selectedWsObj, addRiLabLog]);

  // Fetch user group members when user group is selected
  useEffect(() => {
    const fetchUserGroupMembers = async () => {
      if (!accessToken || !selectedUgObj) {
        setRiLabUserGroupMembers([]);
        return;
      }
      
      const ugNs = selectedUgObj._namespaces?.[0];
      if (!ugNs) return;
      
      try {
        // Fetch users in the user group using IafUserGroup.getUsers
        const ctx = makeCtx(accessToken, [ugNs]);
        const usersResult = await IafUserGroup.getUsers(selectedUgObj, ctx) as unknown;
        
        let users: Array<{ _id: string; _email?: string; _username?: string }> = [];
        if (Array.isArray(usersResult)) {
          users = usersResult;
        } else if (usersResult && typeof usersResult === 'object' && '_list' in usersResult) {
          users = (usersResult as { _list: typeof users })._list || [];
        }
        
        setRiLabUserGroupMembers(users);
        if (users.length > 0) {
          addRiLabLog(`👥 Found ${users.length} users in user group`);
        }
      } catch (err) {
        console.log('Could not fetch user group members:', err);
        setRiLabUserGroupMembers([]);
      }
    };
    
    fetchUserGroupMembers();
  }, [accessToken, selectedUgObj, addRiLabLog]);

  // Create the test collection with collection-level CREATE permission for the user group
  // If forceNew is true, it will create a new collection even if one already exists
  const createRiLabCollection = useCallback(async (forceNew: boolean = false) => {
    if (!accessToken || !selectedWsObj || !selectedUgObj) return;
    
    setRiLabLoading('creating-collection');
    const wsNs = selectedWsObj._namespaces[0];
    const ctx = makeCtx(accessToken, [wsNs]);
    
    try {
      addRiLabLog(`Creating test collection in workspace "${selectedWsObj._name}"...`);
      
      if (!forceNew) {
        // Check if collection already exists
        const existingResult = await IafItemSvc.getNamedUserItems(
          { query: {_userType: 'ri_permission_test_collection' }} ,
          ctx,
          { _pageSize: 10 }
        );
        const existingList = (existingResult?._list || existingResult || []) as Array<{ _id: string; _name: string; _namespaces: string[] }>;
        
        if (existingList.length > 0) {
          const existing = existingList[0];
          addRiLabLog(`Found existing collection: ${existing._name} (${existing._id})`);
          addRiLabLog(`   ⚠️ Using existing collection. Click "Create NEW (CREATE-only)" to create a fresh one.`);
          setRiLabCollection(existing);
          setRiLabLoading(null);
          return;
        }
      } else {
        addRiLabLog(`🆕 Force-creating NEW collection with CREATE-only permission...`);
      }
      
      // Create the collection WITH rowLevelPermissions attribute
      // This attribute tells the permission system to use row-level access control
      const collResult = await IafItemSvc.createNamedUserItems([{
        _name: `RI Permission Test (Row-Level) ${Date.now()}`,
        _shortName: `ri_perm_test`,
        _description: 'Collection for testing Related Item permissions with row-level access control',
        _userType: 'ri_permission_test_collection',
        _namespaces: [wsNs],
        _version: {
          _userAttributes: {
            rowLevelPermissions: ROW_LEVEL_PERMISSIONS_ACTIVE  // This marks it as a row-level permission collection
          }
        }
      }], 'NamedUserCollection', ctx);
      
      const coll = extractFromPage(collResult);
      addRiLabLog(`✅ Created collection: ${coll._name} (${coll._id})`);
      addRiLabLog(`   📋 Set ${ROW_LEVEL_PERMISSIONS_ATTR}="${ROW_LEVEL_PERMISSIONS_ACTIVE}"`);
      
      // Create collection-level permission for the user group (CREATE action ONLY - not READ!)
      addRiLabLog(`Creating collection-level CREATE-ONLY permission for user group "${selectedUgObj._name}"...`);
      addRiLabLog(`   This means users can ADD items but NOT READ them unless they have sub-resource permissions!`);
      addRiLabLog(`   With ${ROW_LEVEL_PERMISSIONS_ATTR}="${ROW_LEVEL_PERMISSIONS_ACTIVE}", the split permission pattern applies.`);
      
      const collectionPermission = {
        _resourceDesc: {
          _irn: `itemsvc:nameduseritem:${coll._id}`
        },
        _actions: ['CREATE'],  // ONLY CREATE - no READ, EDIT, DELETE
        _namespace: wsNs,
        _user: {
          _type: 'usergroup',
          _id: selectedUgObj._id
        }
      };
      
      await IafPermission.createItemPermissions([collectionPermission], ctx);
      addRiLabLog(`✅ Collection-level CREATE-ONLY permission granted to user group`);
      
      setRiLabCollection(coll);
    } catch (err) {
      addRiLabLog(`❌ Error: ${(err as Error).message}`);
    } finally {
      setRiLabLoading(null);
    }
  }, [accessToken, selectedWsObj, selectedUgObj, addRiLabLog]);

  // Delete the RI Lab collection and its permissions, then recreate with proper permissions
  const deleteAndRecreateRiLabCollection = useCallback(async () => {
    if (!accessToken || !selectedWsObj || !selectedUgObj) {
      addRiLabLog('❌ Missing workspace or user group selection');
      return;
    }
    
    setRiLabLoading('deleting-collection');
    const wsNs = selectedWsObj._namespaces[0];
    const ctx = makeCtx(accessToken, [wsNs]);
    
    try {
      addRiLabLog(`🗑️ Starting cleanup and recreate...`);
      
      // 1. Delete existing collection if it exists (NOT touching wildcard permissions!)
      addRiLabLog(`\n📋 Step 1: Deleting existing test collection...`);
      if (riLabCollection) {
        try {

          // Delete the collection itself
          await IafItemSvc.deleteNamedUserItem(riLabCollection._id, ctx);
          addRiLabLog(`   ✅ Deleted collection`);
          setRiLabCollection(null);
          setRiLabItems([]);
        } catch (err) {
          addRiLabLog(`   ⚠️ Error deleting collection: ${(err as Error).message}`);
        }
      } else {
        addRiLabLog(`   No existing collection to delete`);
      }
      
      // 2. Create new collection with proper permissions
      addRiLabLog(`\n📋 Step 2: Creating new collection with CREATE-only permission...`);
      
      await createRiLabCollection();
    
      
    } catch (err) {
      addRiLabLog(`❌ Error: ${(err as Error).message}`);
      console.error('Delete and recreate error:', err);
    } finally {
      setRiLabLoading(null);
    }
  }, [accessToken, selectedWsObj, selectedUgObj, riLabCollection, addRiLabLog, createRiLabCollection]);

  // Add random items WITHOUT permissions
  const addItemsWithoutPermissions = useCallback(async () => {
    if (!accessToken) {
      addRiLabLog('❌ No access token available');
      return;
    }
    if (!riLabCollection) {
      addRiLabLog('❌ No collection selected - create one first');
      return;
    }
    if (!selectedWsObj) {
      addRiLabLog('❌ No workspace selected');
      return;
    }
    
    setRiLabLoading('adding-items-no-perm');
    const wsNs = selectedWsObj._namespaces[0];
    const ctx = makeCtx(accessToken, [wsNs]);
    
    try {
      addRiLabLog(`Adding 2 random items WITHOUT permissions...`);
      
      const items = [
        {
          _name: `No-Perm Item A ${Date.now()}`,
          testData: `Random data ${Math.random().toString(36).substring(7)}`,
          users: [] as string[],
          permissionType: 'none'
        },
        {
          _name: `No-Perm Item B ${Date.now()}`,
          testData: `Random data ${Math.random().toString(36).substring(7)}`,
          users: [] as string[],
          permissionType: 'none'
        }
      ];
      
      for (const item of items) {
        await IafItemSvc.createRelatedItemsBulk(riLabCollection._id, [item], ctx);
        addRiLabLog(`  ✅ Added item: ${item._name}`);
      }
      
      addRiLabLog(`✅ Added ${items.length} items without permissions`);
    } catch (err) {
      addRiLabLog(`❌ Error: ${(err as Error).message}`);
    } finally {
      setRiLabLoading(null);
    }
  }, [accessToken, riLabCollection, selectedWsObj, addRiLabLog]);

  // Add items WITH sub-resource permissions
  const addItemsWithPermissions = useCallback(async () => {
    if (!accessToken) {
      addRiLabLog('❌ No access token available');
      return;
    }
    if (!riLabCollection) {
      addRiLabLog('❌ No collection selected - create one first');
      return;
    }
    if (!selectedWsObj) {
      addRiLabLog('❌ No workspace selected');
      return;
    }
    
    // Try to get user email from various possible properties
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const userAny = user as any;
    const userEmail = userAny?._email || userAny?.email || userAny?._username || 'unknown@user.com';
    const userId = user?._id;
    
    if (!userId) {
      addRiLabLog('❌ Cannot determine user ID');
      console.log('User object for debugging:', user);
      return;
    }
    
    setRiLabLoading('adding-items-with-perm');
    const wsNs = selectedWsObj._namespaces[0];
    const ctx = makeCtx(accessToken, [wsNs]);
    
    try {
      addRiLabLog(`Adding items WITH sub-resource permissions...`);
      addRiLabLog(`User: ${userEmail} (ID: ${userId})`);
      console.log('RI Lab - User object:', user);
      
      // Item 1: Permission by specific itemId
      const item1 = {
        _name: `Perm-by-ItemId ${Date.now()}`,
        testData: `Data ${Math.random().toString(36).substring(7)}`,
        users: [userEmail],
        permissionType: 'itemId'
      };
      
      const item1Result = await IafItemSvc.createRelatedItemsBulk(riLabCollection._id, [item1], ctx) as unknown;
      console.log('RI Lab - item1Result:', item1Result);
      
      // Handle various response formats - API returns { _uris: ['/nameduseritems/.../relateditems/<id>'], _createdCount: 1 }
      let createdItem1Id: string | undefined;
      if (Array.isArray(item1Result) && item1Result[0]?._id) {
        createdItem1Id = item1Result[0]._id;
      } else if (item1Result && typeof item1Result === 'object' && '_list' in item1Result) {
        const list = (item1Result as { _list: { _id: string }[] })._list;
        createdItem1Id = list?.[0]?._id;
      } else if (item1Result && typeof item1Result === 'object' && '_uris' in item1Result) {
        // Extract ID from URI like /nameduseritems/<collection>/relateditems/<itemId>
        const uris = (item1Result as { _uris: string[] })._uris;
        if (uris?.[0]) {
          const parts = uris[0].split('/');
          createdItem1Id = parts[parts.length - 1]; // Last segment is the item ID
        }
      } else if (item1Result && typeof item1Result === 'object' && '_id' in item1Result) {
        createdItem1Id = (item1Result as { _id: string })._id;
      }
      
      if (!createdItem1Id) {
        addRiLabLog(`  ❌ Failed to create item 1 - unexpected response format`);
        console.error('Unexpected item1Result format:', item1Result);
        setRiLabLoading(null);
        return;
      }
      addRiLabLog(`  ✅ Created item 1: ${item1._name} (${createdItem1Id})`);
      
      // Create sub-resource permission by itemId
      const perm1 = {
        _resourceDesc: {
          _irn: `itemsvc:nameduseritem:${riLabCollection._id}`,
          _subresourceDesc: {
            _type: 'relateditem',
            _criteria: {
              _id: createdItem1Id
            }
          }
        },
        _actions: ['READ', 'EDIT', 'DELETE'],
        _namespace: wsNs,
        _user: {
          _type: 'user',
          _id: userId
        }
      };
      
      await IafPermission.createItemPermissions([perm1], ctx);
      addRiLabLog(`  ✅ Created sub-resource permission (by itemId) for item 1`);
      
      // Item 2: Permission by users array ($in expression)
      const item2 = {
        _name: `Perm-by-UsersArray ${Date.now()}`,
        testData: `Data ${Math.random().toString(36).substring(7)}`,
        users: [userEmail],
        permissionType: 'usersArray'
      };
      
      const item2Result = await IafItemSvc.createRelatedItemsBulk(riLabCollection._id, [item2], ctx) as unknown;
      console.log('RI Lab - item2Result:', item2Result);
      
      // Handle various response formats - API returns { _uris: [...], _createdCount: 1 }
      let createdItem2Id: string | undefined;
      if (Array.isArray(item2Result) && item2Result[0]?._id) {
        createdItem2Id = item2Result[0]._id;
      } else if (item2Result && typeof item2Result === 'object' && '_list' in item2Result) {
        const list = (item2Result as { _list: { _id: string }[] })._list;
        createdItem2Id = list?.[0]?._id;
      } else if (item2Result && typeof item2Result === 'object' && '_uris' in item2Result) {
        // Extract ID from URI like /nameduseritems/<collection>/relateditems/<itemId>
        const uris = (item2Result as { _uris: string[] })._uris;
        if (uris?.[0]) {
          const parts = uris[0].split('/');
          createdItem2Id = parts[parts.length - 1]; // Last segment is the item ID
        }
      } else if (item2Result && typeof item2Result === 'object' && '_id' in item2Result) {
        createdItem2Id = (item2Result as { _id: string })._id;
      }
      
      if (!createdItem2Id) {
        addRiLabLog(`  ❌ Failed to create item 2 - unexpected response format`);
        console.error('Unexpected item2Result format:', item2Result);
        setRiLabLoading(null);
        return;
      }
      addRiLabLog(`  ✅ Created item 2: ${item2._name} (${createdItem2Id})`);
      
      // Create sub-resource permission by users array
      const perm2 = {
        _resourceDesc: {
          _irn: `itemsvc:nameduseritem:${riLabCollection._id}`,
          _subresourceDesc: {
            _type: 'relateditem',
            _criteria: {
              users: { $in: [userEmail] }
            }
          }
        },
        _actions: ['READ', 'EDIT', 'DELETE'],
        _namespace: wsNs,
        _user: {
          _type: 'user',
          _id: userId
        }
      };
      
      await IafPermission.createItemPermissions([perm2], ctx);
      addRiLabLog(`  ✅ Created sub-resource permission (by users array) for item 2`);
      
      addRiLabLog(`✅ Added 2 items with different sub-resource permission types`);
      addRiLabLog(`   - Item 1 (${createdItem1Id}): Permission by specific _id`);
      addRiLabLog(`   - Item 2 (${createdItem2Id}): Permission by users: { $in: ["${userEmail}"] }`);
    } catch (err) {
      addRiLabLog(`❌ Error: ${(err as Error).message}`);
    } finally {
      setRiLabLoading(null);
    }
  }, [accessToken, riLabCollection, selectedWsObj, user, addRiLabLog]);

  // Fetch and display items in the collection
  const fetchRiLabItems = useCallback(async () => {
    if (!accessToken || !riLabCollection || !selectedWsObj) return;
    
    setRiLabLoading('fetching-items');
    const wsNs = selectedWsObj._namespaces[0];
    const ctx = makeCtx(accessToken, [wsNs]);
    
    try {
      addRiLabLog(`Fetching items from collection "${riLabCollection._name}"...`);
      
      const itemsResult = await IafItemSvc.getRelatedItems(
        riLabCollection._id,
        {},
        ctx,
        { page: { _pageSize: 100 } }
      );
      
      const items = (itemsResult?._list || itemsResult || []) as Array<{
        _id: string;
        _name?: string;
        users?: string[];
        permissionType?: string;
        [key: string]: unknown;
      }>;
      
      console.log('RI Lab - Fetched items:', items);
      
      addRiLabLog(`✅ Found ${items.length} item(s) visible to current user`);
      setRiLabItems(items.map(item => {
        // Determine permission type - first from property, then infer from name
        let permType: 'itemId' | 'usersArray' | 'none' | undefined = item.permissionType as 'itemId' | 'usersArray' | 'none' | undefined;
        
        // Fallback: infer from item name if permissionType is missing
        if (!permType && item._name) {
          if (item._name.includes('Perm-by-ItemId')) {
            permType = 'itemId';
          } else if (item._name.includes('Perm-by-UsersArray')) {
            permType = 'usersArray';
          } else if (item._name.includes('No-Perm')) {
            permType = 'none';
          }
        }
        
        console.log(`  Item ${item._id}: permissionType="${item.permissionType}" -> resolved="${permType}", name="${item._name}"`);
        
        // Preserve all item properties
        return {
          ...item,
          hasPermission: !!permType && permType !== 'none',
          permissionType: permType
        };
      }));
      setRiLabShowItemsModal(true);
    } catch (err) {
      addRiLabLog(`❌ Error fetching items: ${(err as Error).message}`);
      setRiLabItems([]);
    } finally {
      setRiLabLoading(null);
    }
  }, [accessToken, riLabCollection, selectedWsObj, addRiLabLog]);

  // Share an item with another user in the user group
  const shareItemWithUser = useCallback(async (item: {
    _id: string;
    _name?: string;
    users?: string[];
    permissionType?: 'itemId' | 'usersArray' | 'none';
    testData?: string;
    [key: string]: unknown;
  }) => {
    const targetUserId = riLabShareTarget[item._id];
    if (!targetUserId) {
      addRiLabLog(`❌ Please select a user to share with`);
      return;
    }
    
    if (!accessToken || !riLabCollection || !selectedWsObj) {
      addRiLabLog(`❌ Missing required context for sharing`);
      return;
    }
    
    const targetUser = riLabUserGroupMembers.find(u => u._id === targetUserId);
    if (!targetUser) {
      addRiLabLog(`❌ Selected user not found`);
      return;
    }
    
    const targetEmail = targetUser._email || targetUser._username || 'unknown';
    const wsNs = selectedWsObj._namespaces[0];
    const ctx = makeCtx(accessToken, [wsNs]);
    
    setRiLabLoading(`sharing-${item._id}`);
    
    try {
      if (item.permissionType === 'itemId') {
        // Create sub-resource permission by specific item ID for the target user
        const perm = {
          _resourceDesc: {
            _irn: `itemsvc:nameduseritem:${riLabCollection._id}`,
            _subresourceDesc: {
              _type: 'relateditem',
              _criteria: {
                _id: item._id
              }
            }
          },
          _actions: ['READ', 'EDIT', 'DELETE'],
          _namespace: wsNs,
          _user: {
            _type: 'user',
            _id: targetUserId
          }
        };
        
        await IafPermission.createItemPermissions([perm], ctx);
        addRiLabLog(`✅ Shared "${item._name}" with ${targetEmail} (by itemId permission)`);
        
      } else if (item.permissionType === 'usersArray') {
        // Create sub-resource permission with the target user's email in the users array criteria
        const perm = {
          _resourceDesc: {
            _irn: `itemsvc:nameduseritem:${riLabCollection._id}`,
            _subresourceDesc: {
              _type: 'relateditem',
              _criteria: {
                users: { $in: [targetEmail] }
              }
            }
          },
          _actions: ['READ', 'EDIT', 'DELETE'],
          _namespace: wsNs,
          _user: {
            _type: 'user',
            _id: targetUserId
          }
        };
        
        await IafPermission.createItemPermissions([perm], ctx);
        addRiLabLog(`✅ Shared "${item._name}" with ${targetEmail} (by users array permission)`);
        
        // Also update the item's users array to include the target email
        // Spread all original item properties to preserve them
        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { hasPermission, ...itemProps } = item; // Remove local-only hasPermission field
          await IafItemSvc.updateRelatedItems(riLabCollection._id, [{
            ...itemProps,
            users: [...(item.users || []), targetEmail]
          }], ctx);
          addRiLabLog(`   Also added ${targetEmail} to item's users array`);
        } catch (updateErr) {
          addRiLabLog(`   ⚠️ Could not update item's users array: ${(updateErr as Error).message}`);
        }
        
      } else {
        addRiLabLog(`❌ Item "${item._name}" has no permission type - cannot share`);
        return;
      }
      
      // Clear the share target selection
      setRiLabShareTarget(prev => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });
      
      // Refresh items to show updated data
      await fetchRiLabItems();
      
    } catch (err) {
      addRiLabLog(`❌ Error sharing: ${(err as Error).message}`);
    } finally {
      setRiLabLoading(null);
    }
  }, [accessToken, riLabCollection, selectedWsObj, riLabShareTarget, riLabUserGroupMembers, addRiLabLog, fetchRiLabItems]);

  // Investigate permissions on the collection (admin feature)
  const investigateCollectionPermissions = useCallback(async () => {
    if (!accessToken || !riLabCollection || !selectedWsObj) {
      addRiLabLog('❌ Missing collection or workspace');
      return;
    }
    
    setRiLabLoading('investigating');
    const wsNs = selectedWsObj._namespaces[0];
    
    try {
      addRiLabLog(`🔍 Investigating permissions on collection "${riLabCollection._name}"...`);
      addRiLabLog(`   Collection ID: ${riLabCollection._id}`);
      addRiLabLog(`   Workspace NS: ${wsNs}`);
      
      // 1. Get all permissions for this specific collection (using dot notation for query params)
      addRiLabLog(`\n📋 1. Permissions for specific collection IRN:`);
      console.log(`1. Permissions for specific collection IRN itemsvc:nameduseritem:${riLabCollection._id}:`);
      try {
        const ctx = makeCtx(accessToken, [wsNs]);
        // Use dot notation as per API documentation
        const collPerms = await IafPermission.getItemPermissions({
          '_namespace': wsNs,
          '_resourceDesc._irn': `itemsvc:nameduseritem:${riLabCollection._id}`,
          '_user._id': '*',
          '_user._type': 'usergroup'
        }, ctx) as unknown;
        
        const permList = Array.isArray(collPerms) ? collPerms : 
          (collPerms && typeof collPerms === 'object' && '_list' in collPerms) ? 
            (collPerms as { _list: unknown[] })._list : [];
        
        if (permList.length === 0) {
          addRiLabLog(`   No usergroup permissions found on this collection`);
          console.log(`No usergroup permissions found on this collection`);
        } else {
          console.log(`Permissions found on this collection:`,permList);
          permList.forEach((p: unknown, i: number) => {
            const perm = p as { _user?: { _type?: string; _id?: string }; _actions?: string[]; _resourceDesc?: { _subresourceDesc?: unknown } };
            const hasSubresource = !!perm._resourceDesc?._subresourceDesc;
            addRiLabLog(`   [${i + 1}] ${perm._user?._type}:${perm._user?._id?.substring(0, 8)}... → ${perm._actions?.join(',')} ${hasSubresource ? '(has _subresourceDesc)' : '(collection-level)'}`);
          });
        }
      } catch (err) {
        addRiLabLog(`   ⚠️ Could not fetch (may need SHARE permission): ${(err as Error).message}`);
      }
      
      // 2. Check for wildcard permissions (itemsvc:nameduseritem:*)
      addRiLabLog(`\n📋 2. Wildcard permissions (itemsvc:nameduseritem:*):`);
      console.log(`2. Wildcard permissions (itemsvc:nameduseritem:*):`);
      try {
        const ctx = makeCtx(accessToken, [wsNs]);
        const wildcardPerms = await IafPermission.getItemPermissions({
          '_namespace': wsNs,
          '_resourceDesc._irn': 'itemsvc:nameduseritem:*',
          '_user._id': '*',
          '_user._type': 'usergroup'
        }, ctx) as unknown;
        
        const permList = Array.isArray(wildcardPerms) ? wildcardPerms : 
          (wildcardPerms && typeof wildcardPerms === 'object' && '_list' in wildcardPerms) ? 
            (wildcardPerms as { _list: unknown[] })._list : [];
        
        if (permList.length === 0) {
          addRiLabLog(`   No wildcard usergroup permissions found`);
          console.log(`No wildcard usergroup permissions found`);
        } else {
          console.log(`Wildcard permissions found:`,permList);
          permList.forEach((p: unknown, i: number) => {
            const perm = p as { _user?: { _type?: string; _id?: string }; _actions?: string[] };
            addRiLabLog(`   [${i + 1}] ${perm._user?._type}:${perm._user?._id?.substring(0, 8)}... → ${perm._actions?.join(',')}`);
          });
        }
      } catch (err) {
        addRiLabLog(`   ⚠️ Could not fetch: ${(err as Error).message}`);
      }
      
      // 3. Check for super wildcard (*:*:*)
      addRiLabLog(`\n📋 3. Super wildcard permissions (*:*:*):`);
      console.log(`3. Super wildcard permissions (*:*:*):`);
      try {
        const ctx = makeCtx(accessToken, [wsNs]);
        const superPerms = await IafPermission.getPassPermissions({
          '_namespace': wsNs,
          '_resourceDesc._irn': '*:*:*',
          '_user._id': '*',
          '_user._type': 'usergroup'
        }, ctx) as unknown;
        
        const permList = Array.isArray(superPerms) ? superPerms : 
          (superPerms && typeof superPerms === 'object' && '_list' in superPerms) ? 
            (superPerms as { _list: unknown[] })._list : [];
        
        if (permList.length === 0) {
          addRiLabLog(`   No super wildcard permissions found`);
          console.log(`No super wildcard permissions found`);
        } else {
          console.log(`Super wildcard permissions found:`,permList);
          permList.forEach((p: unknown, i: number) => {
            const perm = p as { _user?: { _type?: string; _id?: string }; _actions?: string[] };
            addRiLabLog(`   [${i + 1}] ${perm._user?._type}:${perm._user?._id?.substring(0, 8)}... → ${perm._actions?.join(',')}`);
          });
        }
      } catch (err) {
        addRiLabLog(`   ⚠️ Could not fetch: ${(err as Error).message}`);
      }
      
      // 4. Check for user group permissions (if selected)
      if (selectedUgObj) {
        addRiLabLog(`\n📋 4. All itemsvc:nameduseritem:* permissions for user group "${selectedUgObj._name}":`);  
        console.log(`4. All itemsvc:nameduseritem:* permissions for user group "${selectedUgObj._name}":`);
        try {
          const ugNs = selectedUgObj._namespaces?.[0];
          const ctx = makeCtx(accessToken, [wsNs, ugNs].filter(Boolean) as string[]);
          
          // Check permissions where this user group is the grantee
          // Must include _resourceDesc._irn - use wildcard to get all item permissions
          const ugPerms = await IafPermission.getItemPermissions({
            '_namespace': wsNs,
            '_resourceDesc._irn': 'itemsvc:nameduseritem:*',
            '_user._id': selectedUgObj._id,
            '_user._type': 'usergroup'
          }, ctx) as unknown;
          
          const permList = Array.isArray(ugPerms) ? ugPerms : 
            (ugPerms && typeof ugPerms === 'object' && '_list' in ugPerms) ? 
              (ugPerms as { _list: unknown[] })._list : [];
          
          if (permList.length === 0) {
            addRiLabLog(`   No item permissions found for this user group`);
            console.log(`No item permissions found for this user group`);
          } else {
            console.log(`Item permissions found for this user group:`,permList);
            permList.forEach((p: unknown, i: number) => {
              const perm = p as { _resourceDesc?: { _irn?: string; _subresourceDesc?: unknown }; _actions?: string[] };
              const hasSubresource = !!perm._resourceDesc?._subresourceDesc;
              addRiLabLog(`   [${i + 1}] ${perm._resourceDesc?._irn} → ${perm._actions?.join(',')} ${hasSubresource ? '(sub-resource)' : ''}`);
            });
          }
        } catch (err) {
          addRiLabLog(`   ⚠️ Could not fetch: ${(err as Error).message}`);
        }
      }
      
      addRiLabLog(`\n💡 TIP: If you see collection-level READ permission without _subresourceDesc,`);
      addRiLabLog(`   that's why all items are visible. Sub-resource permissions only RESTRICT`);
      addRiLabLog(`   access when there's NO broader collection-level permission.`);
      
    } catch (err) {
      addRiLabLog(`❌ Error: ${(err as Error).message}`);
    } finally {
      setRiLabLoading(null);
    }
  }, [accessToken, riLabCollection, selectedWsObj, selectedUgObj, addRiLabLog]);

  // ============================================================================
  // CLEANUP
  // ============================================================================

  const fetchDiagnosticResources = useCallback(async () => {
    if (!accessToken) return;

    setIsFetchingResources(true);
    try {
      const ctx = makeCtx(accessToken, baseNamespaces);

      // Fetch all workspaces and filter client-side for diagnostic ones
      const wsResult = await IafPassSvc.getWorkspaces(
        {},
        ctx,
        { _pageSize: 500 }
      );
      const allWorkspaces = (wsResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
      // Filter to only diagnostic workspaces
      const workspaces = allWorkspaces.filter(ws => ws._userType === 'diag_workspace');

      // Fetch all user groups and filter client-side for diagnostic ones
      const ugResult = await IafPassSvc.getUserGroups(
        {},
        ctx,
        { _pageSize: 500 }
      );
      const allUserGroups = (ugResult?._list || []) as Array<{ _id: string; _name: string; _namespaces: string[]; _userType?: string }>;
      // Filter to only diagnostic user groups
      const userGroups = allUserGroups.filter(ug => 
        ug._userType === 'diag_admin_group' || ug._userType === 'diag_limited_group'
      );

      setServerResources({ workspaces, userGroups });
      console.log(`Fetched diagnostic resources: ${workspaces.length} workspaces, ${userGroups.length} user groups`);
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    } finally {
      setIsFetchingResources(false);
    }
  }, [accessToken, baseNamespaces]);

  const deleteResource = useCallback(async (type: string, id: string) => {
    if (!accessToken || !deleteConfirm) return;

    setIsDeleting(true);
    const ctxBase = makeCtx(accessToken, baseNamespaces);

    try {
      if (type === 'workspace') {
        await IafPassSvc.deleteWorkspace(id, ctxBase);
      } else if (type === 'usergroup') {
        await IafPassSvc.deleteUserGroup(id, ctxBase);
      }
      // Refresh the list
      await fetchDiagnosticResources();
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  }, [accessToken, baseNamespaces, deleteConfirm, fetchDiagnosticResources]);

  // ============================================================================
  // GENERATE FINDINGS
  // ============================================================================

  const generateFindings = useCallback(() => {
    const f: Findings = {
      permissionsVsUserAttributes: '',
      multiWorkspaceAccess: '',
      namespaceFiltering: '',
      userAttributesPurpose: '',
      bestPractices: [],
      whyCreateAccessBetweenUGs: ''
    };

    // Suite A vs Suite B comparison
    if (suiteA.hasRun && suiteB.hasRun) {
      const aSuccess = suiteA.steps.filter(s => s.status === 'success').length;
      const bSuccess = suiteB.steps.filter(s => s.status === 'success').length;

      if (bSuccess >= 4) {
        f.permissionsVsUserAttributes =
          `✅ CONFIRMED: Permissions control access, NOT _userAttributes.\n\n` +
          `Suite A (with _userAttributes): ${aSuccess}/${suiteA.steps.length} steps succeeded\n` +
          `Suite B (without _userAttributes): ${bSuccess}/${suiteB.steps.length} steps succeeded\n\n` +
          `Both suites created collections successfully. Suite B proves that _userAttributes ` +
          `is NOT required for access - it's purely optional UI metadata.`;
      }
    }

    // Suite C analysis
    if (suiteC.hasRun) {
      const c5 = suiteC.steps.find(s => s.stepId === 'C5');
      if (c5?.status === 'success' && c5.whatActuallyHappened?.includes('Multi-workspace access confirmed')) {
        f.multiWorkspaceAccess =
          `✅ CONFIRMED: One user group CAN access multiple workspaces.\n\n` +
          `Suite C demonstrated that a single user group with permissions on multiple ` +
          `workspace namespaces can access resources in all of them.\n\n` +
          `The key is granting permissions in EACH workspace namespace.`;

        f.namespaceFiltering =
          `The nsfilter (ctx._namespaces) controls which data is returned:\n\n` +
          `• nsfilter=[WS1] → Only WS1 data\n` +
          `• nsfilter=[WS2] → Only WS2 data\n` +
          `• nsfilter=[WS1, WS2] → Data from both workspaces\n\n` +
          `This is why portfolio/aggregation views need to include all relevant namespaces.`;
      }
    }

    f.userAttributesPurpose =
      `_userAttributes is OPTIONAL UI METADATA, not access control.\n\n` +
      `Why ipa-core uses it:\n` +
      `• workspace._userAttributes.user_group → Quick lookup of "which groups are for this workspace"\n` +
      `• group._userAttributes.project_workspace → Quick lookup of "which workspace is this group for"\n\n` +
      `Why project_workspace is an OBJECT (not array):\n` +
      `The ipa-core framework was designed assuming 1:1 workspace:usergroup. ` +
      `A better practice would be _userAttributes.workspaces as an ARRAY.\n\n` +
      `In reality, as Suite C shows, a group can access multiple workspaces via permissions.`;

    f.whyCreateAccessBetweenUGs =
      `IafProj.createAccessBetweenUGs() creates permissions so user groups can SEE each other.\n\n` +
      `Without it:\n` +
      `• Users in Group A cannot see that Group B exists\n` +
      `• Team member listings won't work across groups\n` +
      `• Permission management UIs can't reference other groups\n\n` +
      `What it does:\n` +
      `For each pair of groups (A, B), it creates:\n` +
      `• Permission for A to READ Group B's user group resource\n` +
      `• Permission for B to READ Group A's user group resource`;

    f.bestPractices = [
      'PERMISSIONS control access - always create explicit permission grants',
      '_userAttributes is optional UI metadata for navigation convenience',
      'One user group CAN access multiple workspaces - grant permissions in each namespace',
      'Use nsfilter (ctx._namespaces) to control which workspace data is queried',
      'Consider createAccessBetweenUGs for cross-group visibility',
      'For portfolio views, include all relevant workspace namespaces in ctx',
      'Consider _userAttributes.workspaces as array pattern instead of single project_workspace object'
    ];

    setFindings(f);
    setActiveTab('report');
  }, [suiteA, suiteB, suiteC]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    fetchDiagnosticResources();
  }, [fetchDiagnosticResources]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!accessToken) {
    return (
      <div className="diagnostics-page">
        <Header onLogoClick={() => navigate('/')} />
        <div className="diagnostics-content">
          <div className="error-state">
            <i className="fas fa-exclamation-triangle"></i>
            <h2>Authentication Required</h2>
            <p>Please log in to access the diagnostics page.</p>
          </div>
        </div>
      </div>
    );
  }

  const renderStep = (step: StepResult, onRetry?: () => void) => (
    <div key={step.stepId} className={`step-card step-${step.status}`}>
      <div className="step-header">
        <span className={`step-status status-${step.status}`}>
          {step.status === 'pending' && <i className="fas fa-circle"></i>}
          {step.status === 'running' && <i className="fas fa-spinner fa-spin"></i>}
          {step.status === 'success' && <i className="fas fa-check-circle"></i>}
          {step.status === 'failed' && <i className="fas fa-times-circle"></i>}
        </span>
        <span className="step-id">{step.stepId}</span>
        <span className="step-name">{step.stepName}</span>
        {step.elapsed && <span className="step-elapsed">{formatElapsed(step.elapsed)}</span>}
        {step.status === 'failed' && onRetry && (
          <button className="btn-retry" onClick={onRetry}>
            <i className="fas fa-redo"></i> Retry
          </button>
        )}
      </div>

      <div className="step-body">
        <div className="step-info">
          <div className="info-row">
            <span className="label">What this tests:</span>
            <span className="value">{step.whatThisTests}</span>
          </div>
          <div className="info-row">
            <span className="label">Why it matters:</span>
            <span className="value">{step.whyItMatters}</span>
          </div>
          {step.apisUsed && (
            <div className="info-row">
              <span className="label">APIs used:</span>
              <span className="value apis">{step.apisUsed.join(', ')}</span>
            </div>
          )}
          {step.permissionsApplied && (
            <div className="info-row">
              <span className="label">Permissions:</span>
              <span className="value perms">{step.permissionsApplied.join('\n')}</span>
            </div>
          )}
        </div>

        {step.status !== 'pending' && (
          <div className="step-result">
            <div className="info-row">
              <span className="label">Expected:</span>
              <span className="value">{step.whatWeExpect}</span>
            </div>
            {step.whatActuallyHappened && (
              <div className="info-row actual">
                <span className="label">Actual:</span>
                <pre className="value">{step.whatActuallyHappened}</pre>
              </div>
            )}
            {step.error && (
              <div className="info-row error">
                <span className="label">Error:</span>
                <span className="value">{step.error}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="diagnostics-page">
      <Header onLogoClick={() => navigate('/')} />

      <div className="diagnostics-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-main">
            <h1><i className="fas fa-microscope"></i> User Management Lab</h1>
            <p className="subtitle">
              Learn how permissions, _userAttributes, and namespaces work in Twinit Platform
            </p>
          </div>
          <div className="context-info">
            <div className="info-item">
              <span className="label">User</span>
              <span className="value">{user?._email || 'Unknown'}</span>
            </div>
            <div className="info-item">
              <span className="label">App</span>
              <span className="value">{application?._name || 'Unknown'}</span>
            </div>
          </div>
        </div>

        {/* Concepts Section */}
        <div className="concepts-section">
          <h2><i className="fas fa-lightbulb"></i> Key Concepts</h2>
          <div className="concept-panels">
            {Object.entries(CONCEPTS).map(([key, concept]) => (
              <div key={key} className="concept-panel">
                <button
                  className="concept-header"
                  onClick={() => setExpandedConcepts(prev => ({ ...prev, [key]: !prev[key] }))}
                >
                  <span>{concept.title}</span>
                  <i className={`fas fa-chevron-${expandedConcepts[key] ? 'up' : 'down'}`}></i>
                </button>
                {expandedConcepts[key] && (
                  <div className="concept-content">
                    <pre>{concept.content}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs">
          <button className={`tab ${activeTab === 'suiteA' ? 'active' : ''}`} onClick={() => setActiveTab('suiteA')}>
            <i className="fas fa-flask"></i> Suite A: Perms + UA
          </button>
          <button className={`tab ${activeTab === 'suiteB' ? 'active' : ''}`} onClick={() => setActiveTab('suiteB')}>
            <i className="fas fa-flask"></i> Suite B: Perms Only
          </button>
          <button className={`tab ${activeTab === 'suiteC' ? 'active' : ''}`} onClick={() => setActiveTab('suiteC')}>
            <i className="fas fa-flask"></i> Suite C: Multi-WS
          </button>
          <button className={`tab ${activeTab === 'userTest' ? 'active' : ''}`} onClick={() => setActiveTab('userTest')}>
            <i className="fas fa-user"></i> User Perspective
          </button>
          <button className={`tab ${activeTab === 'cleanup' ? 'active' : ''}`} onClick={() => setActiveTab('cleanup')}>
            <i className="fas fa-broom"></i> Cleanup
          </button>
          <button className={`tab ${activeTab === 'report' ? 'active' : ''}`} onClick={() => { generateFindings(); }}>
            <i className="fas fa-file-alt"></i> What We Learned
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Suite A */}
          {activeTab === 'suiteA' && (
            <div className="suite-panel">
              <div className="suite-header">
                <div>
                  <h3>Suite A: Permissions + _userAttributes</h3>
                  <p>Creates workspace, user group, links via _userAttributes, grants permissions, creates collection.</p>
                </div>
                <div className="header-actions">
                  {suiteA.hasRun && (
                    <button className="btn-secondary btn-sm" onClick={() => clearSuiteResults('A')} disabled={suiteA.isRunning}>
                      <i className="fas fa-trash"></i> Clear
                    </button>
                  )}
                  <button className="btn-primary" onClick={runSuiteA} disabled={suiteA.isRunning}>
                    {suiteA.isRunning ? <><i className="fas fa-spinner fa-spin"></i> Running...</> : <><i className="fas fa-play"></i> Run Suite A</>}
                  </button>
                </div>
              </div>
              <div className="steps-list">
                {suiteA.steps.length > 0 ? suiteA.steps.map(step => 
                  renderStep(step, step.stepId === 'A4' && step.status === 'failed' ? retrySuiteAStep4 : undefined)
                ) : (
                  <div className="empty-steps">{suiteA.hasRun ? 'Results cleared' : 'Click "Run Suite A" to start the experiment'}</div>
                )}
              </div>
            </div>
          )}

          {/* Suite B */}
          {activeTab === 'suiteB' && (
            <div className="suite-panel">
              <div className="suite-header">
                <div>
                  <h3>Suite B: Permissions Only (No _userAttributes)</h3>
                  <p>Same as Suite A but WITHOUT _userAttributes linking. Proves permissions alone control access.</p>
                </div>
                <div className="header-actions">
                  {suiteB.hasRun && (
                    <button className="btn-secondary btn-sm" onClick={() => clearSuiteResults('B')} disabled={suiteB.isRunning}>
                      <i className="fas fa-trash"></i> Clear
                    </button>
                  )}
                  <button className="btn-primary" onClick={runSuiteB} disabled={suiteB.isRunning}>
                    {suiteB.isRunning ? <><i className="fas fa-spinner fa-spin"></i> Running...</> : <><i className="fas fa-play"></i> Run Suite B</>}
                  </button>
                </div>
              </div>
              <div className="steps-list">
                {suiteB.steps.length > 0 ? suiteB.steps.map(step => renderStep(step)) : (
                  <div className="empty-steps">{suiteB.hasRun ? 'Results cleared' : 'Click "Run Suite B" to start the experiment'}</div>
                )}
              </div>
            </div>
          )}

          {/* Suite C */}
          {activeTab === 'suiteC' && (
            <div className="suite-panel">
              <div className="suite-header">
                <div>
                  <h3>Suite C: One User Group → Two Workspaces</h3>
                  <p>Creates 2 workspaces, 1 user group with permissions on BOTH, tests namespace filtering.</p>
                </div>
                <div className="header-actions">
                  {suiteC.hasRun && (
                    <button className="btn-secondary btn-sm" onClick={() => clearSuiteResults('C')} disabled={suiteC.isRunning}>
                      <i className="fas fa-trash"></i> Clear
                    </button>
                  )}
                  <button className="btn-primary" onClick={runSuiteC} disabled={suiteC.isRunning}>
                    {suiteC.isRunning ? <><i className="fas fa-spinner fa-spin"></i> Running...</> : <><i className="fas fa-play"></i> Run Suite C</>}
                  </button>
                </div>
              </div>
              <div className="steps-list">
                {suiteC.steps.length > 0 ? suiteC.steps.map(step => renderStep(step)) : (
                  <div className="empty-steps">{suiteC.hasRun ? 'Results cleared' : 'Click "Run Suite C" to start the experiment'}</div>
                )}
              </div>
            </div>
          )}

          {/* User Perspective */}
          {activeTab === 'userTest' && (
            <div className="suite-panel">
              <div className="suite-header">
                <div>
                  <h3>User Perspective</h3>
                  <p>See what the current user can access. Useful for testing with invited users.</p>
                </div>
                <div className="header-actions">
                  <label className="filter-toggle">
                    <input
                      type="checkbox"
                      checked={filterDiagnosticOnly}
                      onChange={(e) => setFilterDiagnosticOnly(e.target.checked)}
                    />
                    <span>Show diagnostic resources only</span>
                  </label>
                  <button className="btn-secondary" onClick={runUserPerspectiveTests} disabled={isRunningUserTest}>
                    <i className="fas fa-sync"></i> Refresh
                  </button>
                  <button className="btn-primary" onClick={runUserPerspectiveTests} disabled={isRunningUserTest}>
                    {isRunningUserTest ? <><i className="fas fa-spinner fa-spin"></i> Running...</> : <><i className="fas fa-search"></i> Run Tests</>}
                  </button>
                </div>
              </div>

              <div className="user-test-results">
                {/* Workspaces Table */}
                <div className="result-section">
                  <h4><i className="fas fa-building"></i> Visible Workspaces ({userTestResults.workspaces.length})</h4>
                  {userTestResults.workspaces.length > 0 ? (
                    <table className="results-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Namespace</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userTestResults.workspaces.map(ws => (
                          <tr key={ws._id}>
                            <td>{ws._name}</td>
                            <td className="ns">{ws._namespaces?.[0]}</td>
                            <td>{ws._userType || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-data">No workspaces found. Run the test to fetch data.</p>
                  )}
                </div>

                {/* User Groups Table */}
                <div className="result-section">
                  <h4><i className="fas fa-users"></i> Visible User Groups ({userTestResults.userGroups.length})</h4>
                  {userTestResults.userGroups.length > 0 ? (
                    <table className="results-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Namespace</th>
                          <th>Type</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userTestResults.userGroups.map(ug => (
                          <tr key={ug._id}>
                            <td>{ug._name}</td>
                            <td className="ns">{ug._namespaces?.[0]}</td>
                            <td>{ug._userType || '-'}</td>
                            <td className="actions-cell">
                              
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-data">No user groups found. Run the test to fetch data.</p>
                  )}
                </div>

                {/* Collections Table */}
                <div className="result-section">
                  <h4><i className="fas fa-folder"></i> Visible Collections ({userTestResults.collections.length})</h4>
                  {userTestResults.collections.length > 0 ? (
                    <table className="results-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Workspace</th>
                          <th>Namespace</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userTestResults.collections.map(c => (
                          <tr key={c._id}>
                            <td>{c._name}</td>
                            <td>{c.workspaceName || '-'}</td>
                            <td className="ns">{c._namespaces?.[0]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-data">No collections found. Run the test to fetch data.</p>
                  )}
                </div>

                {/* Permissions Table */}
                <div className="result-section">
                  <h4><i className="fas fa-key"></i> Permissions ({userTestResults.permissions.length})</h4>
                  {userTestResults.permissions.length > 0 ? (
                    <table className="results-table permissions-table">
                      <thead>
                        <tr>
                          <th>Source</th>
                          <th>IRN (Resource)</th>
                          <th>Actions</th>
                          <th>Target Namespace</th>
                          <th>Granted To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userTestResults.permissions.map((p, idx) => (
                          <tr key={`${p._id || 'perm'}-${p.source || ''}-${idx}`}>
                            <td className="source">{p.source || '-'}</td>
                            <td className="irn">{p._resourceDesc?._irn || '-'}</td>
                            <td className="actions">
                              {p._actions?.map(a => (
                                <span key={a} className={`action-badge action-${a?.toLowerCase()}`}>{a}</span>
                              ))}
                            </td>
                            <td className="ns">{p._namespace || '-'}</td>
                            <td className="user">
                              {p._user ? (
                                <span className={`user-badge ${p._user._type}`}>
                                  {p._user._type}: {p._user._id?.substring(0, 8)}...
                                </span>
                              ) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-data">No permissions found. Run the test to fetch data.</p>
                  )}
                </div>

                {/* User Group Membership */}
                <div className="result-section">
                  <h4><i className="fas fa-id-card"></i> User Group Membership ({userTestResults.userGroupMembership.length})</h4>
                  {userTestResults.userGroupMembership.length > 0 ? (
                    <table className="results-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Namespace</th>
                          <th>ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userTestResults.userGroupMembership.map(ug => (
                          <tr key={ug._id}>
                            <td>{ug._name}</td>
                            <td className="ns">{ug._namespaces?.[0]}</td>
                            <td className="id">{ug._id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-data">No user group membership found. Run the test to fetch data.</p>
                  )}
                </div>

                {/* Test Log */}
                <div className="result-section">
                  <h4><i className="fas fa-terminal"></i> Test Log</h4>
                  <div className="test-log">
                    {userTestResults.testLog.map((log, i) => (
                      <div key={i} className="log-line">{log}</div>
                    ))}
                    {userTestResults.testLog.length === 0 && (
                      <p className="no-data">Run the test to see log output.</p>
                    )}
                  </div>
                </div>

                {/* Related Item Permissions Lab */}
                <div className="result-section ri-lab-section">
                  <h4><i className="fas fa-flask"></i> Related Item Permissions Lab</h4>
                  <p className="section-description">
                    Test row-level permissions using <code>_subresourceDesc</code>. Create a collection with items 
                    that have different permission types to understand how sub-resource permissions work.
                  </p>
                  
                  {/* Workspace and User Group Selection */}
                  <div className="ri-lab-selectors">
                    <div className="selector-group">
                      <label>Workspace (for collection namespace):</label>
                      <select 
                        value={riLabSelectedWorkspace} 
                        onChange={(e) => {
                          setRiLabSelectedWorkspace(e.target.value);
                          setRiLabCollection(null);
                          setRiLabItems([]);
                        }}
                        disabled={!!riLabLoading}
                      >
                        <option value="">-- Select Workspace --</option>
                        {userTestResults.workspaces.map(ws => (
                          <option key={ws._id} value={ws._id}>
                            {ws._name} ({ws._namespaces?.[0]?.substring(0, 12)}...)
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="selector-group">
                      <label>User Group (to grant permissions to):</label>
                      <select 
                        value={riLabSelectedUserGroup} 
                        onChange={(e) => setRiLabSelectedUserGroup(e.target.value)}
                        disabled={!!riLabLoading}
                      >
                        <option value="">-- Select User Group --</option>
                        {userTestResults.userGroups.map(ug => (
                          <option key={ug._id} value={ug._id}>
                            {ug._name} ({ug._id.substring(0, 8)}...)
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Create Collection Button */}
                  {riLabSelectedWorkspace && riLabSelectedUserGroup && !riLabCollection && (
                    <div className="ri-lab-actions">
                      <button 
                        className="btn-primary"
                        onClick={() => createRiLabCollection(false)}
                        disabled={!!riLabLoading}
                      >
                        {riLabLoading === 'creating-collection' 
                          ? <><i className="fas fa-spinner fa-spin"></i> Creating...</>
                          : <><i className="fas fa-plus"></i> Create Test Collection</>
                        }
                      </button>
                      {selectedWsObj && selectedUgObj && (
                        <button
                                className="btn-small btn-restore-ug-perm"
                                onClick={async () => {
                                  if (!accessToken) return;
                                  // Find the first workspace to use as namespace context                                  
                                  const wsNs = selectedWsObj._namespaces[0];
                                  const ctx = makeCtx(accessToken, [wsNs]);
                                  try {
                                    const wildcardPermission = {
                                      _resourceDesc: { _irn: 'itemsvc:nameduseritem:*' },
                                      _actions: ['READ', 'CREATE', 'EDIT', 'DELETE', 'SHARE'],
                                      _namespace: wsNs,
                                      _user: { _type: 'usergroup', _id: selectedUgObj._id }
                                    };
                                    await IafPermission.createItemPermissions([wildcardPermission], ctx);
                                    alert(`✅ Restored wildcard permission for ${selectedUgObj._name}`);
                                  } catch (err) {
                                    alert(`❌ Error: ${(err as Error).message}`);
                                  }
                                }}
                                title="Restore itemsvc:nameduseritem:* with READ,CREATE,EDIT,DELETE"
                              >
                                🔄 Restore Wildcard
                              </button>
                      )}                      
                      <span className="action-hint">
                        Creates a collection with CREATE permission for the selected user group
                      </span>
                    </div>
                  )}
                  
                  {/* Collection Info and Actions */}
                  {riLabCollection && (
                    <div className="ri-lab-collection-panel">
                      <div className="collection-header">
                        <div className="collection-info">
                          <i className="fas fa-folder-open"></i>
                          <span className="collection-name">{riLabCollection._name}</span>
                          <span className="collection-id">({riLabCollection._id.substring(0, 12)}...)</span>
                          <button 
                            className="btn-danger btn-sm btn-delete-recreate"
                            onClick={deleteAndRecreateRiLabCollection}
                            disabled={!!riLabLoading}
                            title="Delete collection, remove wildcard permissions, and recreate with CREATE-only permission"
                          >
                            {riLabLoading === 'deleting-collection' 
                              ? <><i className="fas fa-spinner fa-spin"></i> Cleaning...</>
                              : <><i className="fas fa-sync-alt"></i> 🗑️ Delete &amp; Recreate</>
                            }
                          </button>
                        </div>
                        <div className="collection-actions">
                          <button 
                            className="btn-secondary btn-sm"
                            onClick={addItemsWithoutPermissions}
                            disabled={!!riLabLoading}
                            title="Add items without any sub-resource permissions"
                          >
                            {riLabLoading === 'adding-items-no-perm' 
                              ? <i className="fas fa-spinner fa-spin"></i>
                              : <i className="fas fa-plus-circle"></i>
                            }
                            <span>Add Items (No Perms)</span>
                          </button>
                          
                          <button 
                            className="btn-primary btn-sm"
                            onClick={addItemsWithPermissions}
                            disabled={!!riLabLoading}
                            title="Add items with sub-resource permissions (by itemId and users array)"
                          >
                            {riLabLoading === 'adding-items-with-perm' 
                              ? <i className="fas fa-spinner fa-spin"></i>
                              : <i className="fas fa-shield-alt"></i>
                            }
                            <span>Add Items (With Perms)</span>
                          </button>
                          
                          <button 
                            className="btn-secondary btn-sm"
                            onClick={fetchRiLabItems}
                            disabled={!!riLabLoading}
                            title="View items visible to current user"
                          >
                            {riLabLoading === 'fetching-items' 
                              ? <i className="fas fa-spinner fa-spin"></i>
                              : <i className="fas fa-eye"></i>
                            }
                            <span>View Items</span>
                          </button>
                          
                          <button 
                            className="btn-secondary btn-sm btn-investigate"
                            onClick={investigateCollectionPermissions}
                            disabled={!!riLabLoading}
                            title="Investigate what permissions exist (admin feature)"
                          >
                            {riLabLoading === 'investigating' 
                              ? <i className="fas fa-spinner fa-spin"></i>
                              : <i className="fas fa-search"></i>
                            }
                            <span>🔍 Investigate Perms</span>
                          </button>
                        </div>
                      </div>
                      
                      <div className="permission-info-box">
                        <h5>How This Experiment Works:</h5>
                        <ul>
                          <li><strong>Add Items (No Perms):</strong> Creates items without any sub-resource permissions</li>
                          <li><strong>Add Items (With Perms):</strong> Creates 2 items with different permission types:
                            <ul>
                              <li>Item 1: Permission by specific <code>_id</code> (itemId)</li>
                              <li>Item 2: Permission by <code>users: {'{'} $in: ["{user?._email}"] {'}'}</code></li>
                            </ul>
                          </li>
                          <li><strong>View Items:</strong> Shows which items the current user can actually see</li>
                          <li><strong>🔍 Investigate Perms:</strong> (Admin) Shows all permissions on this collection to debug why users can see items</li>
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  {/* RI Lab Log */}
                  {riLabLog.length > 0 && (
                    <div className="ri-lab-log">
                      <h5>Lab Log:</h5>
                      <div className="test-log">
                        {riLabLog.map((log, i) => (
                          <div key={i} className="log-line">{log}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* RI Lab Items Modal */}
          {riLabShowItemsModal && (
            <div className="modal-overlay" onClick={() => setRiLabShowItemsModal(false)}>
              <div className="modal-content ri-lab-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3><i className="fas fa-list"></i> Items in Collection</h3>
                  <button className="modal-close" onClick={() => setRiLabShowItemsModal(false)}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="modal-body">
                  <p className="modal-description">
                    These are the items visible to the current user ({user?._email}).
                    Items with sub-resource permissions should only be visible if the permission criteria matches.
                  </p>
                  {riLabItems.length > 0 ? (
                    <table className="results-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>ID</th>
                          <th>Users Array</th>
                          <th>Permission Type</th>
                          <th>Share With</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riLabItems.map(item => (
                          <tr key={item._id}>
                            <td>{item._name || '-'}</td>
                            <td className="id">{item._id.substring(0, 12)}...</td>
                            <td>{item.users?.join(', ') || '-'}</td>
                            <td>
                              <span className={`perm-type-badge ${item.permissionType || 'none'}`}>
                                {item.permissionType || 'none'}
                              </span>
                            </td>
                            <td className="share-cell">
                              {(item.permissionType === 'itemId' || item.permissionType === 'usersArray') && riLabUserGroupMembers.length > 0 ? (
                                <div className="share-controls">
                                  <select
                                    value={riLabShareTarget[item._id] || ''}
                                    onChange={(e) => setRiLabShareTarget(prev => ({
                                      ...prev,
                                      [item._id]: e.target.value
                                    }))}
                                    disabled={riLabLoading === `sharing-${item._id}`}
                                  >
                                    <option value="">Select user...</option>
                                    {riLabUserGroupMembers.map(member => (
                                      <option key={member._id} value={member._id}>
                                        {member._email || member._username || member._id.substring(0, 8)}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    className="btn-small btn-share"
                                    onClick={() => shareItemWithUser(item)}
                                    disabled={!riLabShareTarget[item._id] || riLabLoading === `sharing-${item._id}`}
                                  >
                                    {riLabLoading === `sharing-${item._id}` ? '...' : 'Share'}
                                  </button>
                                </div>
                              ) : (
                                <span className="no-share">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-data">No items found. This could mean:
                      <br />• The collection is empty
                      <br />• You don't have permissions to see any items
                    </p>
                  )}
                  <div className="modal-findings">
                    <h5>Observations:</h5>
                    <ul>
                      <li>Items with "none" permission type: {riLabItems.filter(i => i.permissionType === 'none' || !i.permissionType).length}</li>
                      <li>Items with "itemId" permission: {riLabItems.filter(i => i.permissionType === 'itemId').length}</li>
                      <li>Items with "usersArray" permission: {riLabItems.filter(i => i.permissionType === 'usersArray').length}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cleanup */}
          {activeTab === 'cleanup' && (
            <div className="suite-panel">
              <div className="suite-header">
                <div>
                  <h3>Cleanup Diagnostic Resources</h3>
                  <p>Delete workspaces and user groups created by the test suites.</p>
                </div>
                <button className="btn-secondary" onClick={fetchDiagnosticResources} disabled={isFetchingResources}>
                  {isFetchingResources ? <><i className="fas fa-spinner fa-spin"></i> Fetching...</> : <><i className="fas fa-sync"></i> Refresh</>}
                </button>
              </div>

              <div className="cleanup-resources">
                <p className="cleanup-note">
                  <i className="fas fa-info-circle"></i> Only showing resources with <code>_userType: diag_workspace</code> or <code>diag_admin_group</code>
                </p>

                {/* Workspaces */}
                <div className="resource-group">
                  <h4><i className="fas fa-building"></i> Diagnostic Workspaces ({serverResources.workspaces.length})</h4>
                  {serverResources.workspaces.length > 0 ? (
                    <div className="resource-list">
                      {serverResources.workspaces.map(ws => (
                        <div key={ws._id} className="resource-item">
                          <div className="resource-info">
                            <span className="resource-name">{ws._name}</span>
                            <span className="resource-type">{ws._userType}</span>
                            <span className="resource-ns">{ws._namespaces?.[0]}</span>
                          </div>
                          <button
                            className="btn-delete"
                            onClick={() => setDeleteConfirm({ type: 'workspace', id: ws._id, name: ws._name, suite: 'server' })}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">No diagnostic workspaces found.</p>
                  )}
                </div>

                {/* User Groups */}
                <div className="resource-group">
                  <h4><i className="fas fa-users"></i> Diagnostic User Groups ({serverResources.userGroups.length})</h4>
                  {serverResources.userGroups.length > 0 ? (
                    <div className="resource-list">
                      {serverResources.userGroups.map(ug => (
                        <div key={ug._id} className="resource-item">
                          <div className="resource-info">
                            <span className="resource-name">{ug._name}</span>
                            <span className="resource-type">{ug._userType}</span>
                            <span className="resource-ns">{ug._namespaces?.[0]}</span>
                          </div>
                          <button
                            className="btn-delete"
                            onClick={() => setDeleteConfirm({ type: 'usergroup', id: ug._id, name: ug._name, suite: 'server' })}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">No diagnostic user groups found.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Report / What We Learned */}
          {activeTab === 'report' && (
            <div className="suite-panel">
              <div className="suite-header">
                <h3><i className="fas fa-graduation-cap"></i> What We Learned</h3>
              </div>

              {findings ? (
                <div className="findings-content">
                  {/* Permissions vs _userAttributes */}
                  {findings.permissionsVsUserAttributes && (
                    <div className="finding-section">
                      <h4><i className="fas fa-key"></i> Permissions vs _userAttributes</h4>
                      <pre>{findings.permissionsVsUserAttributes}</pre>
                    </div>
                  )}

                  {/* Multi-Workspace Access */}
                  {findings.multiWorkspaceAccess && (
                    <div className="finding-section">
                      <h4><i className="fas fa-building"></i> Multi-Workspace Access</h4>
                      <pre>{findings.multiWorkspaceAccess}</pre>
                    </div>
                  )}

                  {/* Namespace Filtering */}
                  {findings.namespaceFiltering && (
                    <div className="finding-section">
                      <h4><i className="fas fa-filter"></i> Namespace Filtering</h4>
                      <pre>{findings.namespaceFiltering}</pre>
                    </div>
                  )}

                  {/* _userAttributes Purpose */}
                  <div className="finding-section">
                    <h4><i className="fas fa-info-circle"></i> Why _userAttributes Exists</h4>
                    <pre>{findings.userAttributesPurpose}</pre>
                  </div>

                  {/* createAccessBetweenUGs */}
                  <div className="finding-section">
                    <h4><i className="fas fa-link"></i> Why createAccessBetweenUGs()?</h4>
                    <pre>{findings.whyCreateAccessBetweenUGs}</pre>
                  </div>

                  {/* Best Practices */}
                  <div className="finding-section best-practices">
                    <h4><i className="fas fa-star"></i> Best Practices</h4>
                    <ul>
                      {findings.bestPractices.map((bp, i) => (
                        <li key={i}>{bp}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Invitation Test Hint */}
                  <div className="finding-section invitation-hint">
                    <h4><i className="fas fa-envelope"></i> Next Step: Test with an Invited User</h4>
                    <p>
                      To fully validate these findings, invite a test user to all 3 user groups created by the suites.
                      When that user logs in, they should see:
                    </p>
                    <ul>
                      <li>Suite A Workspace + Collection (linked with _userAttributes)</li>
                      <li>Suite B Workspace + Collection (NO _userAttributes, but has permissions)</li>
                      <li>Suite C Workspace 1 + Workspace 2 + both Collections (multi-workspace access)</li>
                    </ul>
                    <p>
                      Use the "User Perspective" tab with the invited user to verify what they can actually see.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="empty-findings">
                  <i className="fas fa-flask"></i>
                  <p>Run at least one suite to generate findings</p>
                  <button className="btn-primary" onClick={() => setActiveTab('suiteA')}>
                    Start with Suite A
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete this {deleteConfirm.type}?</p>
              <p className="modal-item-name">{deleteConfirm.name}</p>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setDeleteConfirm(null)} disabled={isDeleting}>
                  Cancel
                </button>
                <button className="btn-danger" onClick={() => deleteResource(deleteConfirm.type, deleteConfirm.id)} disabled={isDeleting}>
                  {isDeleting ? <><i className="fas fa-spinner fa-spin"></i> Deleting...</> : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkspaceUserGroupDiagnosticsPage;
